export const work = {
  offlineMove: {
    saved: "Saved, will send when online.",
  },
  whoWhen: {
    who: "Who",
    when: "When",
    whenPlaceholder: "Pick a day",
    change: "Change",
    applyToAll: "Apply to all",
    done: "Done",
    pickContractorTitle: "Choose a contractor",
    pickContractorsTitle: "Choose contractors to ask",
    summary: {
      person: "{{name}} does it",
      teamQueue: "Your team, no one picked yet",
      contractor: "{{name}} does it",
      pickContractor: "Choose a contractor",
      pickContractors: "Choose contractors to ask for quotes",
      quotes_one: "Ask {{count}} contractor for a quote",
      quotes_other: "Ask {{count}} contractors for quotes",
    },
    sheetTitle: "Create work",
    title: "Title",
    brief: "Brief",
    moreDetails: "More details",
    create: "Create",
    created: "Work created and handed over",
    quotesSent_one: "Work created. Quote request sent to {{count}} contractor",
    quotesSent_other: "Work created. Quote requests sent to {{count}} contractors",
    notToldTitle: "Assigned, but not told",
    notToldBody: "{{name}} has the work but has no email or account, so nobody told them. Let them know yourself.",
    failedTitle: "Could not create the work",
    failedBody: "Please try again.",
  },
  scope: { contractorWork: "Contractor work" },
  reportsTab: { all: "All reports", versionsTitle: "{{kind}} versions", openReport: "Open {{kind}}", noVersions: "No saved versions yet.", more: "{{n}} more in {{kind}}", withSubJobs: "Jobs with sub-jobs" },
  /** components/work/WorkQueueRow.tsx — one work item as a row (also used on Home). */
  queueRow: {
    secondaryDefault: 'Start',
    you: 'You',
    raisedBy: 'Raised by {{name}}',
    planned: 'Planned {{when}}',
    unassigned: 'Unassigned',
    showFewerStatusLabels: 'Show fewer status labels',
    showMoreStatusLabels_one: 'Show {{count}} more status label: {{labels}}',
    showMoreStatusLabels_other: 'Show {{count}} more status labels: {{labels}}',
    moreActionsForA11y: 'More actions for {{title}}',
    secondaryActionA11y: '{{action}} {{title}}',
    openItemA11y: 'Open {{title}}',
    open: 'Open',
  },
  /**
   * lib/work/jobChecks.ts + components/work/JobChecksSection.tsx — job
   * prechecks/postchecks. Only the pure-display strings are keyed here:
   * `derivePostcheckFromFindings`'s name/item labels and the job-report
   * builder's title/status strings are persisted onto real check + report
   * records (server-visible content), not local-only display text, so they
   * stay hardcoded English pending a design decision on translating stored
   * content.
   */
  jobChecks: {
    inline: {
      hide: "Hide answers",
      loading: "Loading the answers…",
      loadFailed: "The answers could not be loaded. Open the job again to retry.",
      failed: "Failed",
      skipped: "Skipped",
      photos_one: "{{count}} photo",
      photos_other: "{{count}} photos",
    },
    stageLabel: {
      pre: 'Precheck',
      post: 'Postcheck',
      standalone: 'Check',
    },
    stageLabelLower: {
      pre: "precheck",
      post: "postcheck",
      standalone: "check",
    },
    round: "Round {{n}}",
    noneYet: "None yet",
    removeStageA11y: "Remove {{stage}}",
    attachFailedTitle: "Job created, but the {{stage}} could not be attached",
    attachFailedBody: "Add it from the job.",
    attach: {
      addAs: "Add as {{stage}}",
      chooseChecklist: "Choose a checklist",
      attachTemplateAsA11y: "Attach {{name}} as a {{stage}}",
      chooseFirstA11y: "Choose a checklist first",
      attachAsA11y: "Attach as {{stage}}",
      loadingTitle: "Loading your checklists…",
      loadingBody: "One moment — fetching the library.",
      noMatchTitle: "No checklists match that",
      noMatchBody: "Try a shorter search, or clear it to see everything.",
      emptyTitle: "No checklists to attach yet",
      emptyBody: "Any checklist can be used as a check. Build one and it will appear here.",
    },
    resultLabel: {
      pass: 'Pass',
      flag: 'Flag',
      fail: 'Fail',
    },
    // Pluralizes on the TOTAL (a 1-step check stays "1 step" whether 0 or 1 of
    // it is done), so `count` here is bound to `total`, not `completed`.
    stepCount_one: '{{completed}}/{{count}} step',
    stepCount_other: '{{completed}}/{{count}} steps',
    gateReasonSingle: 'The {{stage}} “{{name}}” must be completed {{verb}}.',
    gateReasonMultiple: '{{count}} {{noun}} must be completed {{verb}}.',
    checksNoun: 'checks',
    beforeSubmitted: 'before this job is submitted',
    beforeStarted: 'before this job starts',
    gateDefaultReason: 'A check must be completed first.',
    templateStepCount_one: '{{count}} step',
    templateStepCount_other: '{{count}} steps',
    historyOneFailure: '1 failure across {{span}}.',
    historyFailures_one: '{{count}} failure across {{span}}.',
    historyFailures_other: '{{count}} failures across {{span}}.',
    historyOneFlag: '1 item flagged across {{span}}.',
    historyFlags_one: '{{count}} item flagged across {{span}}.',
    historyFlags_other: '{{count}} items flagged across {{span}}.',
    historyNoFailures: 'No failures across {{span}}.',
    spanLastCheck: 'the last check',
    spanLastChecks_one: 'the last check',
    spanLastChecks_other: 'the last {{count}} checks',
    completedStatus: 'Completed {{progress}}',
    notFinishedStatus: '{{progress}} — not finished',
    reworkRequested: 'Rework requested',
    section: {
      heading: 'What this job needs',
      addCheckA11y: 'Add a check to this job',
      addCheck: 'Add check',
      tryAgainA11y: 'Try loading checks again',
      tryAgain: 'Try again',
      noChecksTitle: 'No checks on this job',
      // On a maintenance plan: the checks go on every job the plan raises.
      headingPlan: 'What each job needs',
      noChecksTitlePlan: 'No checks yet',
      noChecksBody: 'Attach a checklist to run before the work starts or after it finishes, and its answers become the evidence this job was done.',
      required: 'Required',
      round: 'Round {{number}}',
      stop: 'Stop {{number}}: {{name}}',
      checkFallback: 'Check',
      openCheckA11y: '{{verb}} {{stage}} {{name}}. {{progress}}.',
      review: 'Review',
      continueVerb: 'Continue',
      start: 'Start',
      reviewCheck: 'Review check',
      continueCheck: 'Continue check',
      startCheck: 'Start check',
      removeCheckA11y: 'Remove {{stage}} {{name}}',
      remove: 'Remove',
    },
  },
  /** components/work/RequestActivityStream.tsx — the request's unified conversation + audit feed. */
  requestActivityStream: {
    tidyFailedBody: "Try again in a moment, or send it as it is.",
    tidyFailedTitle: "Could not tidy up the message",
    tidiedReview: "Check it before you send it.",
    tidiedTitle: "Tidied-up message",
    tidyUp: "Tidy up with AI",
    stop: "Stop",
    noAccountHint: "{{name}} has no account, so they will not see messages here. Use Ask a question to email them.",
    notSent: "Not sent. Check your connection and press Send again.",
    theRequesterDefault: 'The requester',
    theTeamDefault: 'The team',
    raisedThis: '{{who}} raised this · {{time}}',
    you: 'You',
    noMessagesYetOperator: 'No messages yet — reply below to start the conversation.',
    noMessagesYetRequester: 'No messages yet — send one below and the team will reply here.',
    removePhotoA11y: 'Remove photo',
    replyOrLogNoteA11y: "Message the requester",
    messageTeamA11y: 'Message the team',
    replyOrLogNotePlaceholder: "Message the requester (they see this)…",
    messageTeamPlaceholder: 'Message the team…',
    sending: 'Sending…',
    send: 'Send',
    sendReplyA11y: 'Send reply',
    signInRequiredTitle: 'Sign in required',
    signInToAttachPhotos: 'Sign in to attach photos.',
    uploadFailedTitle: 'Upload failed',
    couldNotAttachPhoto: 'Could not attach the photo.',
  },
  /** components/work/RequestDetailHeader.tsx — the request case's record header. */
  requestDetailHeader: {
    noPriority: 'No priority',
    assetNotSet: 'Asset not set',
    notSet: 'Not set',
    tileLabel: {
      asset: 'Asset',
      requester: 'Requester',
      priority: 'Priority',
      scheduled: 'Scheduled',
      respondBy: 'Respond by',
    },
    openTileA11y: 'Open {{label}}: {{value}}',
    escalated: 'Escalated · 48h+',
    nextPrefix: 'Next: ',
  },
  /** components/work/RepairDecisionPanel.tsx — the requester's repair-approval instrument. */
  repairDecisionPanel: {
    yourDecisions: 'Your decisions',
    progress: '{{decided}} of {{total}} decided',
    approveRecommendationTitle: "Agree with every item?",
    approveRecommendationHint: "Marks every item approved. You can still decline any before you sign.",
    applying: 'Applying...',
    approveRecommended: "Approve all",
    urgent: 'Urgent',
    highPriority: 'High priority',
    noEvidence: 'No evidence',
    possibleDuplicate: 'Possible duplicate',
    approved: 'Approved',
    declined: 'Declined',
    awaitingDecision: 'Awaiting decision',
    approve: "Approve",
    decline: 'Decline',
    approveItemA11y: 'Approve {{label}}',
    declineItemA11y: 'Decline {{label}}',
    seeEvidenceA11y: 'See the evidence for {{label}}',
    openAllEvidenceA11y: 'Open all evidence for {{label}}',
    evidenceThumbA11y: '{{label}} evidence {{number}}',
    declineNoteA11y: 'Decline note for {{label}}',
    declineNotePlaceholder: 'Why are you declining? (optional — shared with the team)',
    photoCount_one: '{{count}} photo',
    photoCount_other: '{{count}} photos',
    videoCountClause_one: ' · {{count}} video',
    videoCountClause_other: ' · {{count}} videos',
    viewDetailsClause: ' · View details',
    approvedTotal: 'Approved total: {{amount}}',
    nothingApprovedYet: 'Nothing approved yet',
  },
  /** components/workIntelligence/WorkIntelligenceActions.tsx — the shared AI entry-point row. */
  workIntelligenceActions: {
    analyzeEvidence: 'Read the photos',
    useComment: 'Use comment',
    draftComment: 'Draft comment',
    suggestAction: 'Suggest action',
    useSuggestion: 'Use suggestion',
    createJobFromSuggestion: 'Create job from suggestion',
    createJob: 'Create job',
    assessEvidence: 'Assess evidence',
    useNote: 'Use note',
    couldNotCompleteRequest: 'Could not complete the request.',
    unexpectedError: 'Unexpected error',
    aiSuggestionWithConfidence: 'AI suggestion · {{percent}}% confident',
    aiSuggestion: 'AI suggestion',
    priorityLabel: 'Priority: {{priority}}',
    riskLabel: 'Risk: {{band}} ({{score}}/100)',
    dismissSuggestionA11y: 'Dismiss suggestion',
    dismiss: 'Dismiss',
  },
  /** components/work/ReportAccessRequestsSection.tsx — pending report-access requests. */
  reportAccessRequests: {
    roleLabel: {
      owner: 'Owner',
      tenant: 'Tenant / occupant',
      landlord: 'Landlord',
      manager: 'Managing agent',
      client: 'Client',
      contractor: 'Contractor',
      other: 'Other',
    },
    headerCount_one: 'Report access request · {{count}}',
    headerCount_other: 'Report access requests · {{count}}',
    someoneFallback: 'Someone',
    assetFallback: 'Asset',
    approve: 'Approve',
    decline: 'Decline',
    couldNotCompleteTitle: 'Could not complete',
    tryAgainMoment: 'Please try again in a moment.',
    alreadyDecidedTitle: 'Already decided',
    alreadyDecidedMessage: 'Someone else has already answered this request.',
    notPermittedTitle: 'Not permitted',
    notPermittedMessage: 'You do not have permission to decide this request.',
    accessApproved: 'Report access approved',
    requestDeclined: 'Request declined',
  },
  /** components/work/AssetRequestDetailScreen.tsx — the request case detail screen. */
  assetRequestDetailScreen: {
    actions: {
      openJob: 'Open job',
      openInspection: 'Open inspection',
      approve: 'Approve',
      replyFirstTitle: "Mark as replied and continue?",
      replyFirstBody: "This case is waiting on the customer. Continuing records that they replied, then carries on.",
      replyFirstCancel: "Not now",
      replyFirstConfirm: "Mark as replied",
      decline: 'Decline',
      replied: "Log their reply",
      ask: "Ask a question",
      inspect: "Inspect",
      createWork: "Create a job",
      close: "Close request",
    },
    assistant: {
      title: 'Assistant',
      turnIntoWork: 'Turn into work',
      suggestNextSteps: 'Suggest next steps',
      requestAssistantTitle: 'Request assistant',
      promptWithTitle: 'Help me review this request (“{{title}}”) and decide whether to ask, inspect, create work, or close it.',
      promptDefault: 'Help me review this request and decide whether to ask, inspect, create work, or close it.',
    },
    capture: {
      notSavedTitle: 'Capture not saved',
      notFoundMessage: 'That capture could not be found. Please capture it again.',
      mismatchMessage: 'This capture does not match the current request.',
      notSupportedTitle: 'Capture not supported',
      scanNotSupportedMessage: 'QR scan is handled from the scanner, not saved to requests.',
      tryAgain: "Check your connection and try again.",
      noPhotoCaptured: 'No photo was captured.',
      signInToAttachPhotos: 'Sign in to attach photos.',
      couldNotUploadPhoto: 'Could not upload the photo.',
      noCommentTextCaptured: 'No comment text was captured.',
      modeNotSupported: 'This capture mode is not supported for requests.',
      photoAdded: 'Photo added to request',
      commentAdded: 'Request comment added',
      measurementAdded: 'Measurement added to request comments',
      measurementCommentTemplate: 'Measurement: {{reading}}',
    },
    toast: {
      approved: "Request approved and the requester told. Next, tap Create work or Inspect to plan it.",
      declined: "Request declined. The requester has been told.",
      caseClosed: "Request closed. The requester has been told.",
      replyRecorded: "Reply noted. The request is back with you to act on.",
      messageSent: "Question sent. The request waits for their answer, and you will be told when they reply.",
      caseReopened: "Request reopened. It is back in Work under Needs you.",
    },
    screenTitle: "Request",
    loadingMessage: 'Loading request...',
    notFoundMessage: 'Request not found',
    notFoundHint: 'This request may have been removed or you may not have access.',
    requestFallbackTitle: 'Request',
    reopenFailedTitle: "Could not reopen the request",
  },
  /** components/work/RequestCaseLinkedWork.tsx — the request case's "Linked work" section. */
  requestCaseLinkedWork: {
    statusLabel: {
      scheduled: 'Scheduled',
      toSchedule: 'To schedule',
      due: 'Due',
      overdue: 'Overdue',
      inProgress: 'In progress',
      paused: 'Paused',
      submitted: 'Submitted',
      completedAwaitingSignoff: 'Completed — awaiting sign-off',
      done: 'Done',
      reworkInProgress: 'Rework in progress',
      cancelled: 'Cancelled',
      pending: 'Pending',
    },
    reportStatus: {
      sharedReadyForReview: 'Shared — ready for review',
      approved: 'Approved',
      changesRequested: 'Changes requested',
      reviewed: 'Reviewed',
    },
    provenance: {
      youApproved: 'You approved this work',
      approvedByRequester: 'Approved by the requester',
      youDeclined: 'You declined this — raise it in the conversation',
      declinedByRequester: 'Declined by the requester',
      followUpProposed: 'Follow-up proposed by the team',
      followUpJob: 'Follow-up job',
      addedByTeamNotFromReview: 'Added by the team — not from your review',
      addedByTeam: 'Added by the team',
    },
    repair: {
      fallbackLabel: 'Repair {{number}}',
      approved: 'Approved',
      declined: 'Declined',
      awaitingDecision: 'Awaiting decision',
      jobState: 'Job: {{status}}',
      noJobYet: 'No job yet',
    },
    heading: 'Linked work',
    inspectionTitle: 'Inspection',
    completed: 'Completed',
    jobFallbackTitle: 'Job',
    reportTitle: 'Inspection report',
    youAcceptedAwaitingTeam: 'You accepted — awaiting team confirmation',
    acceptedByRequesterVerify: 'Accepted by requester — verify to close',
    visitConfirmed: 'Visit confirmed',
    visitConfirmedByRequester: 'Visit confirmed by requester',
    confirmVisitBelow: 'Confirm the visit below',
    awaitingRequesterVisitConfirmation: 'Awaiting requester visit confirmation',
    photoCount_one: '{{count}} photo',
    photoCount_other: '{{count}} photos',
    approvedRepairsAwaitingJobs_one: '{{count}} approved repair awaiting jobs',
    approvedRepairsAwaitingJobs_other: '{{count}} approved repairs awaiting jobs',
    createApprovedJobs_one: 'Create {{count}} approved job',
    createApprovedJobs_other: 'Create {{count}} approved jobs',
    confirmVisit: 'Confirm visit',
    confirmVisitA11y: 'Confirm someone can be there for the visit',
    requestDifferentTime: 'Request different time',
    requestDifferentTimeA11y: 'Ask for a different visit time',
    reviewCompletionReport: 'Review completion report',
    reviewAndSignOff: "Review and sign off",
    viewCompletionReport: 'View completion report',
    openCompletionReportA11y: 'Open the job completion report',
    acceptCompletion: "Accept the work",
    acceptCompletionA11y: 'Accept the completed work',
    requestRework: "Send back",
    requestReworkA11y: "Send the job back to be fixed",
    createJobsFromRepairsA11y: 'Create jobs from the approved repairs',
    viewReportResponse: 'View report response',
    reviewSharedReport: 'Review shared report',
    viewYourReportResponseA11y: 'View your report response',
    reviewSharedReportA11y: 'Review the shared report',
  },
  /** components/work/WorkDetailRoute.tsx — the unified job/inspection detail dispatcher. */
  detailRoute: {
    notFoundTitle: 'Work detail',
    notFoundMessage: 'Work item not found',
    notFoundHint: 'Open this job or inspection from Work, Assets, or a valid shared link.',
  },
  /** components/work/WorkKanbanSwimlanes.tsx — the asset-lane kanban board. */
  kanbanSwimlanes: {
    emptyDefault: 'No items in this view.',
    unassigned: 'Unassigned',
    assetColumnHeader: 'Asset',
  },
  /** components/work/WorkQueueCardGrid.tsx — the card-grid queue layout. */
  queueCardGrid: {
    emptyDefault: 'No items in queue',
    open: 'Open',
    start: 'Start',
    openItemA11y: 'Open {{title}}',
    startItemA11y: 'Start {{title}}',
  },
  /** components/work/StatusTransitionBar.tsx — the job status control row. */
  statusTransitionBar: {
    currentA11y: 'Status: {{label}}',
    moveToA11y: 'Move to {{label}}',
    changeStatusA11y: 'Status: {{label}}. Change status',
    sheetTitle: 'Change status',
  },
  /** lib/work/stalled.ts + components/work/StalledTakeoverPanel.tsx — a decision waiting on someone who went quiet. */
  stall: {
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    waitingOnYourApproval: 'Waiting on your approval for {{span}}.',
    waitingOnYourValidation: 'Waiting on your validation for {{span}}.',
    noResponseFrom: 'No response from {{name}} for {{span}}.',
    notValidated: '{{name}} has not validated this for {{span}}.',
    notApproved: '{{name}} has not approved this for {{span}}.',
    theRecipient: 'the recipient',
    theRequester: 'the requester',
    theApprover: 'the approver',
    theValidator: 'the validator',
    approveOnTheirBehalf: 'Approve on their behalf',
    validateInTheirPlace: 'Validate in their place',
    hintDecide: 'As the asset team you can decide for them. Both are recorded as made on their behalf.',
    hintValidate: 'As the asset team you can validate this in their place.',
    hintSignOff: 'As the asset team you can sign this off in their place.',
    saving: 'Saving…',
  },
  /** components/work/TriageInboxCard.tsx — inline triage row for the Requests segment. */
  triageInbox: {
    titleDefault: 'New',
    emptyLabelDefault: 'No new requests.',
    cleared: 'All requests decided',
    approve: 'Approve',
    reject: 'Reject',
  },
  /** components/work/WorkNameTypeFilter.tsx defaults. */
  nameTypeFilter: {
    typeDefault: 'Type',
    pickerTitleDefault: 'Filter by type',
    any: 'any',
    typeA11y: '{{label}}: {{value}}',
  },
  /** components/work/WorkSortMenu.tsx — the shared sort trigger + sheet. */
  sortMenu: {
    triggerA11y: 'Sort: {{label}}',
    sortBy: 'Sort by',
  },
  /** components/work/WorkViewToggle.tsx — the cards/table/kanban/calendar switcher. */
  viewToggle: {
    cards: 'Cards',
    table: 'Table',
    kanban: 'Kanban',
    calendar: 'Calendar',
  },
  /** The unified Work list surface (quick views, type chips, Refine, states). */
  // Reason codes carried by LifecycleAttentionItem. The canonical record is the
  // CODE; these are its rendering. Nothing may read a translated string back to
  // decide severity or grouping — that is the bug this namespace exists to end.
  attentionReasons: {
    OVERDUE: 'Overdue',
    AWAITING_YOU: 'Waiting on you',
    AWAITING_OTHER: 'Waiting on someone else',
    UNSCHEDULED: 'Not scheduled yet',
    DUE_SOON: 'Due soon',
    URGENT_PRIORITY: 'Marked urgent',
    APPROVAL_REQUIRED: 'Waiting for your approval',
    REVIEW_REQUIRED: 'Waiting for your review',
    REPAIR_JOBS_REQUIRED: 'Approved repairs need jobs',
    STALLED: 'No response — you can step in',
  },
  list: {
    board: {
      moveItemA11y: "Move {{title}}",
      moveSelectedA11y: "Move {{n}} to {{column}}",
      bulkSummaryBlocked: "Move {{n}} to {{column}}. {{blocked}} can’t move from where they are.",
      bulkSummary: "Move {{n}} to {{column}}.",
      requestMoveReason: "Requests move through approve, decline or convert — open it to decide.",
      inspectionMoveReason: "Inspections move by being carried out — open it to continue.",
      moveNotAllowedTitle: "Move not allowed",
      moveNotAllowed: "That move is not allowed.",
      cannotMoveStraight: "{{from}} work can’t move straight to {{to}}.",
    },
    selectedA11y: "Selected",
    emptyState: {
      showMeHow: "Show me how",
      scheduleInspection: "Schedule an inspection",
      none: "No work here yet. Schedule an inspection to get started.",
      showAll: "Show all work",
      review: "Nothing is waiting for a review.",
      due_soon: "Nothing is due soon.",
      mine: "Nothing is assigned to you right now.",
      needs_attention: "Nothing needs you right now.",
      clearFilters: "Clear filters",
      filtered: "Nothing matches your Refine choices.",
    },
    simpleStatus: {
      todo: "To do",
      in_progress: "In progress",
      waiting: "Waiting",
      done: "Done",
      waitingFor: "Waiting for {{name}}",
      waitingOn: "Waiting · {{who}}",
      waitingForAssignee: "Waiting for the assignee",
      waitingForRequester: "Waiting for the requester",
      waitingForReview: "Waiting for review",
      waitingForQuotes: "Waiting for quotes",
      waitingForApproval: "Waiting for approval",
    },
    quickView: {
      // The app's word for the ACT set (Home tile, hero eyebrow, bell group) —
      // the Work lens counted the same rows under a different name.
      needs_attention: 'Needs you',
      mine: 'Mine',
      due_soon: 'Due soon',
      review: 'Review',
      all: 'All',
    },
    reviewScope: {
      yours: "Yours",
      everyone: "Everyone's",
    },
    /** Phone: the one view-swap button names the view it switches TO. */
    view: {
      calendarShort: "Calendar",
      listShort: "List",
      title: "View",
      cards: 'List view',
      calendar: 'Calendar view',
    },
    type: {
      request: 'Requests',
      inspection: 'Inspections',
      job: 'Jobs',
    },
    sort: {
      urgency: 'Urgency',
      due: 'Due date',
      updated: 'Recently updated',
      priority: 'Priority',
      created: 'Newest',
    },
    group: {
      asset: 'Asset',
      site: 'Site',
      assignee: 'Assignee',
      status: 'Status',
      type: 'Type',
      none: 'None',
    },
    /** Group-header count suffix, e.g. "3 · 1 urgent" on an asset cluster. */
    groupUrgent_one: '{{count}} urgent',
    groupUrgent_other: '{{count}} urgent',
    groupByLabel: "Group: {{axis}}",
    groupByTitle: "Group work by",
    collapseAll: "Collapse all",
    expandAll: "Expand all",
    collapseGroup: "Collapse {{name}} ({{count}})",
    expandGroup: "Expand {{name}} ({{count}})",
    groupOverdue: "Overdue: {{count}}",
    groupNeedsYou: "Needs you: {{count}}",
    groupDueSoon: "Due soon: {{count}}",
    groupNew: "New: {{count}}",
    groupTypeDocument: "Documents",
    groupNone: { asset: "No asset", site: "No site", assignee: "Unassigned" },
    newLane: { title: "New since you looked", more: "Show {{count}} more", fewer: "Show fewer" },
    /** Accessibility label for the tap-through asset group header. */
    openAsset: 'Open {{name}}',
    report: {
      all: 'Any',
      has_report: 'Has report',
      awaiting_review: 'Awaiting review',
      signed_off: 'Signed off',
    },
    refine: {
      button: 'Refine',
      output: 'Output',
      type: 'Type',
      asset: 'Asset',
      site: 'Site',
      assignee: 'Assignee',
      unassigned: 'Unassigned',
      urgency: 'Urgency',
      status: 'Status',
      review: 'Review',
      reviewToggle: 'Requires my review',
      time: 'Time',
      renewals: 'Renewals',
      renewalsToggle: 'Show renewals',
      sortBy: 'Sort by',
      // Adaptive selector (used when a dimension has too many values for chips).
      anyAsset: 'Any asset',
      unnamedSite: 'Unnamed site',
      unnamedAssignee: 'Unnamed person',
      anySite: 'Any site',
      anyone: 'Anyone',
      // Facet chips above the Assignee list: is this work with our own people,
      // with someone outside the workspace, or with nobody yet.
      assigneeFacetTeam: 'Team',
      assigneeFacetExternal: 'External',
      selectedCount_one: '{{count}} selected',
      selectedCount_other: '{{count}} selected',
      searchPlaceholder: 'Search…',
      searchAssetPlaceholder: 'Search assets…',
      removeAsset: 'Remove {{name}}',
      searchSitePlaceholder: 'Search sites…',
      searchAssigneePlaceholder: 'Search people…',
      noMatches: 'No matches.',
      clear: 'Clear',
      clearSelection: 'Clear selection',
      done: 'Done',
    },
    timePreset: {
      overdue: 'Overdue',
      today: 'Today',
      next_7: 'Next 7 days',
    },
    urgencyBand: {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    },
    status: {
      triage: 'New',
      scheduled: 'Scheduled',
      in_progress: 'In progress',
      awaiting_review: 'Awaiting review',
      done: 'Done',
      cancelled: 'Cancelled',
    },
    kanbanEmpty: {
      triage: 'No new requests.',
      scheduled: 'Nothing scheduled.',
      in_progress: 'Nothing in progress.',
      awaiting_review: 'Nothing awaiting review.',
      done: 'Nothing done yet.',
      cancelled: 'Nothing cancelled.',
      board: "No work in flight for this view.",
    },
    due: {
      inDays: "Due in {{n}}d",
      overdueDays: "{{n}}d overdue",
      overdue: 'Overdue',
      today: 'Due today',
      tomorrow: 'Due tomorrow',
      onDate: 'Due {{date}}',
    },
    badge: {
      urgency: { critical: 'Urgent', high: 'High' },
      rework: 'Sent back',
      quotesDeclined: 'All declined',
      quotesWaiting: 'Awaiting quotes',
      awaitingRequester: 'Awaiting requester',
      subJobsDone: '{{done}} of {{total}} sub-jobs done',
      subJobNeedsYou: 'Sub-job needs you',
      partOf: 'Part of {{title}}',
      partOfAJob: 'Part of a bigger job',
      confirmVisit: 'Confirm the visit',
      visitUnconfirmed: 'Visit unconfirmed',
      acceptancePending: 'Accept or decline',
      awaitingAcceptance: 'Awaiting acceptance',
      awaitingVerification: 'Awaiting verification',
      quoteInvited: 'Quote invitation',
      attention: {
        overdue: 'Overdue',
        awaiting_you: 'Awaiting you',
        stalled: 'Stalled',
        unscheduled: 'Unscheduled',
        expiring_soon: 'Due soon',
      },
      /** components/work/workItemBridge.ts — a live row with no other badge says its column. */
      column: {
        triage: 'Open',
        scheduled: 'Scheduled',
        in_progress: 'In progress',
        awaiting_review: 'Awaiting review',
      },
      review: {
        awaiting_your_approval: 'Approve',
        you_must_review: 'Review',
        awaiting_recipient: 'Awaiting reply',
        changes_requested: 'Changes requested',
      },
      report: {
        awaiting_repairs_conversion: 'Approved repairs',
        pending_signoff: 'Report ready',
        signed_off: 'Signed off',
      },
      done: {
        completed: 'Closed',
        verified: 'Signed off',
        rejected: 'Declined',
        cancelled: 'Cancelled',
        superseded: 'Superseded',
        skipped: 'Skipped',
      },
    },
    empty: 'Nothing here right now.',
    emptyReports: 'No reports here yet. A report appears once an inspection or job has one.',
    previewEmpty: 'Select a job or request to manage it here.',
    loadError: "Some work couldn't be loaded ({{sources}}).",
    retry: 'Retry',
    loadingA11y: 'Loading Work',
    /** The lightweight record preview shown in the docked panel. */
    preview: {
      kind: {
        job: 'Job',
        request: 'Request',
        inspection: 'Inspection',
        schedule: 'Inspection',
        document: 'Document',
      },
      asset: 'Asset',
      status: 'Status',
      due: 'Due',
      noDue: 'No due date',
      assigned: 'Assigned',
      unassigned: 'Unassigned',
      site: 'Site',
      report: 'Report',
      reportAvailable: 'Available',
      reportNone: 'Not available',
      openFull: 'Open full record',
      openFullKind: 'Open {{kind}}',
      pin: 'Pin panel',
      unpin: 'Unpin panel',
      collapse: 'Collapse panel',
    },
  },
  /** Actionable "Suggested next steps" card (plan → confirm → run). */
  aiNextSteps: {
    summaryReady: '{{ready}} of {{total}} ready to run',
    createdMore: '+{{count}} more',
    openCreatedA11y: 'Open {{label}}',
    recordKind: {
      job: 'View job',
      inspection_schedule: 'View scheduled inspection',
      request: 'View request',
      template: 'View checklist',
    },
    title: 'Suggested next steps',
    planning: 'Working out the best next steps…',
    running: 'Running the steps you confirmed…',
    empty: 'Nothing needs doing here right now.',
    doAll: 'Do all ({{total}})',
    doStep: 'Do this',
    doStepA11y: 'Do this step: {{step}}',
    retry: 'Try again',
    dismiss: 'Dismiss',
    errorFallback: 'Unable to suggest next steps.',
    blocked: {
      advisory: 'Guidance only — nothing to run automatically.',
      missingDetails: 'Needs a few more details before this can run.',
    },
    outcome: {
      executed: 'Done',
      failed: 'This step did not complete.',
    },
    results: {
      allDone: 'All confirmed steps completed ({{total}}).',
      partial: '{{executed}} completed, {{failed}} failed.',
    },
    confirm: {
      title: 'Confirm what will happen',
      intro: 'This will run now and change your records ({{total}}):',
      noDetail: 'No further detail available.',
      run: 'Run now',
      cancel: 'Cancel',
    },
  },
  workflow: {
    actions: {
      requestedReview: 'Review requested',
      approvedRepairs: 'Approved repairs',
      assignedWork: 'Assigned work',
      scheduleProposed: 'Schedule proposed',
      workStarted: 'Work started',
      workPaused: 'Work paused',
      workResumed: 'Work resumed',
      completionSubmitted: 'Completion submitted',
      reworkRequested: 'Rework requested',
      workApproved: 'Work approved',
      jobAccepted: 'Job accepted',
      jobDeclined: 'Job rejected',
      quoteRequestSent: 'Quote request sent',
      quoteSubmitted: 'Quote submitted',
      quoteAccepted: 'Quote accepted',
      quoteRejected: 'Quote rejected',
      contractorAwarded: 'Contractor awarded',
    },
  },
  search: {
    title: 'Search Work',
    placeholder: 'Search inspections, jobs, requests or assets',
    triggerA11y: 'Search Work',
    triggerHint: 'Search inspections, jobs, requests, or assets',
    inputA11y: 'Search inspections, jobs, requests, or assets',
    closeA11y: 'Close Work search',
    clearA11y: 'Clear Work search',
    scope: {
      all: 'All',
      inspections: 'Inspections',
      jobs: 'Jobs',
      requests: 'Requests',
    },
    // Shown ONLY while a scope is applied (set by a group's "View all"), and
    // tapping it clears back to all types. The permanent copy of the hub's type
    // tabs that used to sit here is gone.
    scopeActive: '{{type}} only ✕',
    groups: {
      inspection: 'Inspections',
      job: 'Jobs',
      request: 'Requests',
      asset: 'Assets',
      contact: 'Contacts',
    },
    refine: 'Refine',
    refineCount: 'Refine {{count}}',
    refineA11y_one: 'Refine Work search, {{count}} active filter category',
    refineA11y_other: 'Refine Work search, {{count}} active filter categories',
    clearFilters: 'Clear all',
    filters: {
      state: 'State',
      scope: 'Scope',
      all: 'All',
      open: 'Open',
      completed: 'Completed',
      mine: 'Assigned to me',
      team: 'My team',
      needsAttention: 'Needs attention',
    },
    minimumTitle: 'Search all Work',
    recentsTitle: 'Recent',
    recentsClear: 'Clear',
    pinA11y: 'Pin to keep at the top',
    unpinA11y: 'Unpin',
    minimumBody: 'Enter at least two characters to search authorised inspections, jobs, requests, and assets.',
    loading: 'Searching Work…',
    errorTitle: 'Search is unavailable',
    errorBody: 'We could not search Work. Check your connection and try again.',
    noResultsTitle: 'No matching work',
    noResultsBody: 'Try a different title, reference, asset, location, or assignee.',
    viewAll: 'View all',
    viewAllA11y: 'View all {{type}} results',
    result: {
      type: {
        inspection: 'Inspection',
        job: 'Job',
        request: 'Request',
        asset: 'Asset',
        contact: 'Contact',
      },
      statusA11y: 'Status {{status}}',
      dateA11y: 'Date {{date}}',
      assigneeA11y: 'Assigned to {{name}}',
    },
  },
  hub: {
    title: 'Work',
    // Impact-filter banner (deep link from the relationship graph's "View
    // affected work"). `count` drives the pluralized asset noun.
    impactTitle: "Showing some assets only",
    impactBody_one: "This list shows work for {{count}} asset only.",
    impactBody_other: "This list shows work for {{count}} assets only.",
    impactClear: "Show all",
    impactClearA11y: "Show work for every asset",
    // No `subtitle`: the Work hub passes `subtitle={undefined}` deliberately —
    // the line only restated what the segment tabs already show. The key
    // outlived its last reader and is not re-added without a consumer.
    tablistLabel: 'Filter Work by type: Inspections, Jobs, or Requests',
    // "View all" on an empty type — see WorkSegmentViews `emptyAction`.
    emptyViewAll: 'View all',
    segments: {
      queue: 'My Queue',
      inspections: 'Inspections',
      jobs: 'Jobs',
      approvals: 'Approvals',
      requests: 'Requests',
      templates: "Inspection checklists",
    },
    hints: {
      queue: 'Priority work and decisions',
      inspections: 'Inspections, reviews, and schedules',
      jobs: 'Jobs and workload',
      approvals: 'Review inbound requests and completed work waiting on a decision',
      requests: 'Triage inbound requests; approve, schedule, or convert to inspections or jobs',
      templates: 'Reusable inspection checklists',
      // The strip filters one list instead of switching between four screens,
      // so each hint has to say that selecting again clears the filter — that
      // is the way back to everything that needs you.
      inspectionsFilter:
        'Filter Work to inspections, reviews, and schedules. Select again to show everything that needs you.',
      jobsFilter: 'Filter Work to jobs. Select again to show everything that needs you.',
      requestsFilter:
        'Filter Work to inbound requests. Select again to show everything that needs you.',
    },
    secondaryLabel: 'Secondary',
    workflowLibrary: 'Checklists',
    workflowLibraryShort: 'Checklists',
    workflowLibraryHint: 'Reusable inspection checklists',
    workflowLibraryA11y: 'Open checklists',
    workflowLibraryBack: 'Back to Work',
    workflowLibraryBackHint: 'Leave checklists',
    workflowLibraryBackA11y: 'Return to work queue',
    secondaryToolsTitle: 'Operational reports',
    secondaryToolsSummary: 'Reports and analytics.',
    secondaryToolsMeta: '1 secondary tool',
    status: {
      attention: 'Needs attention',
      inspections: 'Inspections open',
      jobs: 'Jobs open',
      approvals: 'Approvals',
      requests: 'Requests',
    },
    nextAction: {
      eyebrow: 'Recommended next',
      reviewApprovals: 'Review approvals',
      reviewApprovalsMeta: 'Decide which inbound requests and completed work move next.',
      // "My Queue" is no longer a tab the user can see — it is the landing
      // state you get by clearing the type filter. A label naming an invisible
      // destination is worse than one naming what actually happens.
      openQueue: 'Show everything that needs you',
      openQueueMeta: 'Overdue, due today, and blocked.',
      continueInspections: 'Continue inspections',
      continueInspectionsMeta: 'Planned, active, and complete.',
      continueJobs: 'Continue jobs',
      continueJobsMeta: 'Owners, blockers, dates, status.',
      backToQueue: 'Back to Work',
      backToQueueMeta: 'Return to everything that needs you.',
      openWorkflows: 'Open checklists',
      openWorkflowsMeta: 'Create or reuse inspection checklists.',
    },
  },
  queue: {
    mobileFilter: {
      all: 'All',
      overdue: 'Overdue',
      today: 'Today',
      approvals: 'Approvals',
      a11y: 'Filter queue: {{label}}',
    },
    // The banner shown when the queue is narrowed to one section by `?focus=`
    // and no visible pill already says so.
    focus: {
      showing: 'Showing {{label}} · {{count}}',
      inProgress: 'Work in progress',
      attention: 'Needs you',
      clear: 'Clear',
      clearA11y: 'Clear filter and show the whole queue',
      showAll: 'Show everything',
    },
    // "Nothing in your queue" is false under a filter — the queue can be full
    // and this one slice empty. Name the slice instead.
    emptyFocused: 'Nothing here: {{label}}.',
    // Refine › Group by. Wide layouts only: the phone routes every view except
    // the calendar to the sectioned list, which has no grouping axis to set.
    refineGroupBy: 'GROUP BY',
    groupBy: {
      asset: 'By asset',
      status: 'By status',
    },
    table: {
      type: 'Type',
    },
    calendar: {
      today: 'Today',
      todayA11y: 'Jump to today',
      prevMonthA11y: 'Previous month',
      nextMonthA11y: 'Next month',
      moreCount: '+{{count}} more',
      emptyDay: "Nothing planned this day",
      dayA11y: "{{date}} — {{count}} planned: {{summary}}",
      dayEmptyA11y: "{{date}} — nothing planned",
      summaryAndMore: '{{titles}} and {{count}} more',
      openItemA11y: 'Open {{type}}: {{title}}',
      unscheduledTitle: 'Unscheduled · {{count}}',
      unscheduledHint: "No date yet. Open one to give it a date.",
      scheduleWork: 'Schedule work',
      scheduleSheetTitle: 'What do you want to schedule?',
      scheduleInspection: 'Schedule inspection',
      scheduleJob: 'Schedule job',
      filters: {
        all: 'All',
        jobs: 'Jobs',
        inspections: 'Inspections',
        requests: 'Requests',
        renewals: 'Documents',
      },
      prevRangeA11y: 'Previous two weeks',
      nextRangeA11y: 'Next two weeks',
      upcoming: 'Upcoming',
      modeMonth: 'Month',
      modeWeek: 'Week',
      modeMonthA11y: 'Show the month grid',
      modeWeekA11y: 'Show the week by hour',
      prevWeekA11y: 'Previous week',
      nextWeekA11y: 'Next week',
      allDay: 'All day',
      weekItemA11y: '{{type}} at {{time}}: {{title}}',
    },
    itemKinds: {
      job: 'Job',
      inspection: 'Inspection',
      request: 'Request',
      document: 'Document',
      condition: 'Condition',
      activity: 'Activity',
      quick_action: 'Quick action',
    },
    empty: 'Nothing in your queue.',
    emptyLine: 'Nothing in your queue for this scope.',
    emptyBrowse: 'Open Inspections',
    emptyBrowseA11y: 'Open the Inspections register',
    searchPlaceholder: 'Find by asset, ID, owner',
    ungroupedSite: 'Other',
    hintFullLists: 'Use Inspections and Jobs for full registers.',
    context: {
      followUpTitle: 'Follow-up',
      toolsTitle: 'Checklists',
      reportIssue: 'Report issue',
      flagProblem: 'Flag problem',
      requestFix: 'Request fix',
      escalate: 'Escalate',
      reusePrevious: 'Reuse previous',
      moreFollowUps: 'More',
      moreFollowUpsSheetTitle: 'Log follow-up or workflow',
    },
    sections: {
      queueTitle: 'Queue',
      overdue: 'Overdue · {{count}}',
      dueToday: 'Due today · {{count}}',
      certificateActions: 'Certificates due · {{count}}',
      scheduledInspections: 'Scheduled inspections · {{count}}',
      inProgressJobs: 'In progress jobs · {{count}}',
      needsAttention: 'Needs attention · {{count}}',
      reportReviews: 'Report reviews',
    },
  },
  assetCollaboration: {
    respondBy: {
      days: "{{count}}d",
      hours: "{{count}}h",
      minutes: "{{count}}m",
      overdue: "Overdue {{window}}",
      dueIn: "Due in {{window}}",
    },
    requestType: {
      job: "Job",
      inspection: "Inspection",
      help: "Help",
    },
    requestPriority: {
      urgent: "Urgent",
      high: "High",
      medium: "Medium",
      low: "Low",
    },
    activityLabels: {
      lifecycleRecorded: "Recorded",
      lifecycleBulk: "Bulk change",
      team: "The team",
      shareRole: {
        requester: "Requester",
        viewer: "Viewer",
      },
      shareStatus: {
        revoked: "Revoked",
        declined: "Declined",
        active: "Active",
        pending: "Awaiting acceptance",
      },
      sharedWithContact: "Shared with a contact",
      sharedWith: "Shared with {{name}}",
      documentStatus: {
        no_expiry: "No expiry date",
        expired: "Expired",
        expiring_soon: "Expiring soon",
        valid: "Valid",
      },
      connectedState: {
        planned: "Logged",
        outstanding: "Needs attention",
        completed: "Resolved",
      },
      connectedSource: "Connected source",
      inspector: "Inspector",
      unassigned: "Unassigned",
      scheduledInspection: "Scheduled inspection",
      type: {
        lifecycle: "Lifecycle",
        location: "Location",
        access: "Access",
        connected: "Connected system",
        document: "Document",
        job: "Job",
        inspection: "Inspection",
      },
    },
    breadcrumbRequests: "Requests",
    evidenceFromRequest: "Photos from the request",
    conversation: {
      empty: "No messages yet. Send one below — {{who}} will see it and reply here.",
      noneExchanged: "No messages were sent on this request.",
    },
    closedBanner: {
      declinedRequester: "This request was closed without going ahead.",
      declinedOp: "This request was declined or withdrawn.",
      doneRequester: "All done — you accepted the work. Nothing more to do.",
      doneOp: "The work is done and signed off. This request is complete.",
      teamClosed: "The team closed this request.",
      closedByTeam: "Your team closed this request. Tap Reopen if there is more to do.",
      closedByYou: "You closed this request. Tap Reopen if there is more to do.",
      titleNotProceeding: "Closed · Not going ahead",
      titleDone: "Closed · Done",
    },
    tracker: {
      next: "Next:",
      active: "Open {{age}}",
      closedTook: "Closed · took {{age}}",
      closed: "Closed",
      progressA11yNow: "Request progress: {{done}} of {{total}} steps done, now {{stage}}",
      progressA11y: "Request progress: {{done}} of {{total}} steps done",
    },
    row: {
      noAccountA11y: "reported without an account",
      noAccount: "No account",
      contact: "Contact",
      unassigned: "Unassigned",
      team: "Team",
      assigned: "Assigned",
      preferred: "Preferred",
      scheduled: "Scheduled",
    },
    showAll_other: "Show all ({{count}})",
    showAll_one: "Show all ({{count}})",
    showFewer: "Show fewer",
    messages: {
      newReplies_other: "{{count}} new replies from the team",
      newReplies_one: "{{count}} new reply from the team",
      open: "Message the team",
      hide: "Hide messages",
    },
    period: {
      pm: "Afternoon",
      am: "Morning",
    },
    withdraw: {
      confirm: "Withdraw",
      keep: "Keep it",
      body: "This cancels the request and any planned visit, and tells the team.",
      title: "Withdraw this request?",
      button: "Withdraw request",
    },
    badge: {
      request: "Request",
      job: "Job",
      inspection: "Inspection",
    },
    showDetailsA11y: "{{title}}. Show details",
    hideDetailsA11y: "{{title}}. Hide details",
    collapseHide: "Hide",
    expandShow: "Show",
    emptyAssetRequester: "You have not reported anything on this asset yet.",
    sectionPendingRequester: "Sent — the team is looking at it",
    summary: {
      declined_other: "{{count}} declined",
      declined_one: "{{count}} declined",
      accepted_other: "{{count}} accepted",
      accepted_one: "{{count}} accepted",
      planned_other: "{{count}} planned",
      planned_one: "{{count}} planned",
      inProgress_other: "{{count}} in progress",
      inProgress_one: "{{count}} in progress",
      withTeam_other: "{{count}} with the team",
      withTeam_one: "{{count}} with the team",
      needsReply_other: "{{count}} need your reply",
      needsReply_one: "{{count}} needs your reply",
      toLookAt_other: "{{count}} to look at",
      toLookAt_one: "{{count}} to look at",
    },
    progress: {
      reports_other: "{{count}} reports",
      reports_one: "{{count}} report",
      inspections_other: "{{count}} inspections",
      inspections_one: "{{count}} inspection",
      jobs_other: "{{count}} jobs",
      jobs_one: "{{count}} job",
    },
    sort: {
      updated: "Recently updated",
      oldest: "Oldest",
      newest: "Newest",
    },
    displayStatus: {
      requester: {
        planned: "Planned",
        accepted: "Accepted",
        needsReview: "Needs your review",
        declined: "Declined",
        closed: "Closed",
        waiting: "Needs your reply",
        active: "In progress",
        triaged: "Approved",
        new: "Sent",
      },
      operator: {
        declined: "Declined",
        closed: "Closed",
        waiting: "Waiting on the requester",
        active: "In progress",
        triaged: "Approved",
        new: "New",
      },
    },
    issueReportedConfirmInspection: "Issue logged. Pick the checklist and a time to book the inspection.",
    issueReportedConfirmJob: "Issue logged. Check the job below, then create it.",
    updateFailedTitle: "The request was not updated",
    visitProposed: "Time proposed. The requester is asked to confirm it.",
    requester: {
      contactMissing: "We could not connect your account to this asset. Ask the person who shared it to share it again.",
      assetMissing: "We could not find this asset. Open the link you were sent again.",
      submitFailedTitle: "Your report was not sent",
      calendarTitle: "Inspection: {{name}}",
      reworkReason: "The requester asked for the work to be redone.",
      tryAgain: "Check your connection and try again.",
      declineShareFailed: "Not declined",
      acceptShareFailed: "Not accepted",
      withdrawFailed: "The request was not withdrawn",
      sendFailed: "Not sent",
      confirmFailed: "Not confirmed",
      reviewFailed: "Your answer was not saved",
      rescheduleFailed: "Your new time was not sent",
      confirmVisitFailed: "The visit was not confirmed",
      reportSent: "Report sent. The team will look at it; updates appear here and you get a notification.",
      withdrawn: "Request withdrawn. The team has been told.",
      preferredTimeSent: "Sent. The team will look at your preferred time and get back to you.",
      reworkRequested: "Sent back to the team. They have your note and will come back to it.",
      completionAccepted: "Thanks — you accepted the work. The team will close the request.",
      rescheduleSent: "Asked for a different time. The team will suggest a new one.",
      visitConfirmed: "Visit confirmed. The team has been told.",
    },
    journey: {
      naReviewReport: "Review the report and accept the work",
      naWaitingReviewOp: "Waiting on the requester to review",
      naTeamCloses: "The team confirms and closes the work",
      naVerify: "Verify the completed work",
      naWorkUnderway: "Work is underway",
      naWillSchedule: "The team will schedule the work",
      naVisitAsPlanned: "The team will visit as planned",
      naConfirmVisit: "Confirm someone can be there for the visit",
      naCarryOut: "Carry out the work",
      naScheduleJob: "Schedule and start the job",
      naHelpOp: "Reply to the requester, or create a job",
      naStartInspection: "Start the inspection",
      naTeamArranging: "The team is arranging the work",
      naPlanWork: "Plan the work",
      naCreateJob: "Create the job",
      naScheduleInspection: "Schedule or start the inspection",
      naTeamReviewing: "The team is reviewing your request",
      naTriage: "Approve, decline or ask a question",
      naWaitingOnRequester: "Waiting on the requester",
      naReplyToTeam: "Reply to the team",
      detailTeamNote: "Team note: {{notes}}",
      detailReason: "Reason: {{notes}}",
      slNewRequestOp: "New request.",
      slAllDone: "All done.",
      slCompletedOp: "Completed.",
      slReadyForReview: "Ready for your review.",
      slWaitingReviewOp: "Waiting on the requester to review.",
      slAccepted: "You accepted the work — the team confirms and closes.",
      slAcceptedOp: "Accepted by the requester — verify and close.",
      slWorkUnderway: "The work is underway.",
      slWorkUnderwayOp: "Work underway.",
      slJobCreated: "A job has been created.",
      slJobCreatedOp: "Job created — not yet scheduled.",
      slVisitPlanned: "Visit planned for {{when}}.",
      slJobPlannedOp: "Job planned for {{when}}.",
      slHelp: "The team is helping with this.",
      slHelpOp: "Approved — reply, or create a job.",
      slInspectionUnderway: "An inspection is underway.",
      slInspectionUnderwayOp: "Inspection underway.",
      slInspectionScheduled: "Inspection scheduled for {{when}}.",
      slInspectionBookedOp: "Inspection booked for {{when}}.",
      slApproved: "Approved — the team is arranging the work.",
      slApprovedOp: "Approved — arrange the work.",
      slSubmitted: "Submitted.",
      slNewOp: "New — look at this request.",
      slWaitingOnRequester: "Waiting on the requester for more information.",
      slWaitingOnYou: "The team needs some more information from you.",
      slDeclined: "This request was declined.",
      slDeclinedOp: "Declined.",
      stDone: "Done",
      stClose: "Close",
      stYourReview: "Your review",
      stReport: "Report",
      stSignOff: "Sign-off",
      stAnswered: "Answered",
      stAnswer: "Answer",
      stInProgress: "In progress",
      stWork: "Work",
      stInspecting: "Inspecting",
      stInspect: "Inspect",
      stPlanned: "Planned",
      stSchedule: "Schedule",
      stReviewed: "Reviewed",
      stTriage: "Review",
      stRaised: "Raised",
      stReceived: "Received",
    },
    requestConvert: {
      choose: "Choose what happens next",
      inspectMeta: "Pick the checklist and a time. The inspection is booked and stays linked to the request.",
      jobMeta: "Check who does it, by when and the instructions, then create the job.",
      inspectInstead: "Inspect instead",
      inspect: "Inspect",
      jobInstead: "Create a job instead",
      job: "Create a job",
      subtitle: "The request details are filled in for you. You only confirm who does it and when.",
      title: "What should happen next?",
    },
    requestFulfilment: {
      unclear: "It is not clear what is wrong, so an inspection will show what work is needed.",
      urgentUnclear: "It is urgent but unclear, so send someone now and let them check on site.",
      readsAsCheck: "It reads like a check of condition or safety, so inspect first.",
      describesFault: "It describes something to fix, so a job is the quickest route.",
      askedForJob: "They asked for a job to be done.",
      askedForInspection: "They asked for an inspection.",
      assessFirst: "Check it first, so you know what work is needed.",
    },
    tabLabel: 'Follow-ups',
    tabTitle: 'Follow-ups & triage',
    tabIntro:
      'Log follow-ups or review requests from shared contacts.',
    openApprovalsQueue: "See all requests in Work",
    sectionCardTitle: 'Requests on this asset',
    sectionCardSub: "Log a problem, or see every request in Work.",
    sectionCardSubRequester: 'Report an issue and follow what happens to it.',
    newRequest: 'Raise a request',
    composeItemTitle: 'Inspection checklist item',
    composeItemDescription: 'Referenced checklist item: {{itemId}}',
    activityFollowUpLabel: 'Logged item',
    activityAssetLabel: 'Asset',
    activityAssetCreatedTitle: 'Asset added',
    activityAssetCreatedStatus: 'Created',
    activityAssistantChangeTitle: 'Changed via the assistant',
    activityAssistantUndoneTitle: 'Assistant change undone',
    activityAssistantStatus: 'Confirmed in the assistant',
    activitySiteLabel: "Site",
    missingAssetForCompose: 'This asset could not be resolved.',
    missingLinkedContactForCompose: "We could not log this issue under your name. Sign out and back in, then try again.",
    operatorLinkedContactNoAccount:
      'Your profile is not linked to a workspace account yet. Try signing out and back in. If this continues, contact support.',
    operatorLinkedContactAutoFailed: "We could not log this issue under your name. Try again in a moment; if it keeps happening, sign out and back in.",
    composeSubmitErrorTitle: 'Unable to submit',
    issueReported: "Issue logged. Approve it, or tap Create a job or Inspect to plan the work.",
    composeSubmitErrorBody: 'Please try again.',
    composeOpenErrorTitle: 'Cannot open',
    sectionPending: "New",
    sectionAwaitingCustomer: "Needs your reply",
    sectionAwaitingCustomerInternal: "Waiting on the requester",
    sectionActive: "Approved, planned or in progress",
    sectionClosed: 'Closed',
    sectionRejected: 'Declined',
    emptyAsset: "Nothing reported on this asset yet. Tap Raise a request to log a problem.",
    loadingFollowUps: 'Loading follow-ups…',
    emptySection: 'Nothing in this group.',
    actionApprove: 'Approve',
    actionReject: 'Decline',
    actionSchedule: 'Schedule',
    actionConvert: 'Choose work path',
    metaContactFallback: 'Contact',
    metaNoPriority: 'No priority',
    metaNoDate: 'No preferred timing',
    outcomeTypeJob: 'Tracked job',
    outcomeTypeInspection: 'Scheduled inspection',
  },
  followUpDetail: {
    managerActionsTitle: 'Manager actions',
    managerApprove: 'Approve',
    managerReject: 'Reject',
    managerSchedule: 'Schedule',
    managerConvertJob: 'Convert to Job',
    managerConvertInspection: 'Convert to Inspection',
    summaryAssetHeadline: 'Asset',
    summaryRequesterHeadline: 'Requester',
    emptyLoadingTitle: 'Loading',
    emptyLoadingBody: 'Pulling the latest summary and audit trail.',
    emptySelectTitle: 'Select an item',
    emptySelectBody: 'Select an item to view details and actions.',
    outcomeKindJob: 'Tracked as work',
    outcomeKindInspection: 'Inspection requested',
    outcomeSummary: 'Summary',
    fieldStatus: 'Status',
    fieldPriority: 'Priority',
    fieldProposedDate: 'Preferred timing',
    fieldScheduledFor: 'Scheduled for',
    fieldResolution: 'Resolution notes',
    assetSummary: 'Asset',
    fieldAssetName: 'Asset',
    fieldAssetType: 'Type',
    fieldAssetStatus: 'Asset status',
    reporter: 'Submitted by',
    fieldContactName: 'Name',
    fieldContactEmail: 'Email',
    timeline: 'Progress',
    timelinePending: 'Pending',
    timelineDone: 'Done',
    history: 'Audit trail',
    historyEmpty: 'No audit entries yet.',
    linkedWork: 'Linked work',
    openLinkedJob: 'Open linked job',
    openLinkedInspection: 'Open planned visit',
    actions: 'Next steps',
    actionApprove: 'Approve',
    actionReject: 'Decline',
    actionSchedule: 'Schedule',
    actionConvert: 'Choose work path',
    loggedContext: 'Where this came from',
    fieldSourceInspection: 'Inspection',
    fieldSourceJob: 'Job',
    fieldSourceSchedule: 'Planned visit',
    fieldSourceLocation: 'Location note',
    fieldIntent: 'How it was logged',
    intentReport: 'Report issue',
    intentFlag: 'Flag problem',
    intentRequestFix: 'Request fix',
    intentEscalate: 'Escalate',
    historyActionSubmitted: 'Logged for triage',
    historyActionApproved: 'Approved',
    historyActionRejected: 'Declined',
    historyActionScheduled: 'Scheduled',
    historyActionConvertedJob: 'Job prepared from request',
    historyActionConvertedInspection: 'Inspection prepared from request',
    historyActionCommented: 'Comment added',
    aiAssistDisclosureTitle: 'Assist & drafts',
    aiAssistDisclosureSummary: 'Summaries, rewrites, and suggested responses',
  },
  approvalsWorkspace: {
    toolbarOpenAssetFollowUps: 'Open asset follow-ups',
    toolbarChooseAsset: 'Choose asset',
    searchPlaceholder: 'Search by asset id',
    applyAssetFilter: 'Apply asset filter',
    applyTeamFilter: 'Apply team filter',
    listColumnFollowUp: 'Follow-up',
    listColumnStatus: 'Status',
    listColumnPriority: 'Priority',
    listColumnDate: 'Date',
    listPaneTitle: 'Requests',
    webStatusHint: '',
    detailPaneTitle: 'Detail',
    mobileTitle: 'Requests',
    mobileSubtitle:
      'Triage follow-ups and prepare work.',
    loadingQueue: 'Loading approvals…',
    emptyQueue: 'No requests here',
    emptyQueueHint: 'Try the Sent or All segments, or widen scope.',
    filterInspection: 'Inspection',
    filterJob: 'Job',
    countPending: 'Pending',
    countUrgent: 'Urgent',
    filterAssetPlaceholder: 'Filter by asset id',
    filterTeamPlaceholder: 'Filter by team id',
    statusFilterAll: 'All',
    statusFilterPending: 'Pending',
    statusFilterApproved: 'Approved',
    statusFilterScheduled: 'Scheduled',
    statusFilterConverted: 'Linked work',
    statusFilterRejected: 'Declined',
    refine: 'Refine',
    refineA11y: 'Refine request filters',
    refineSheetTitle: 'Refine requests',
    nameSearchPlaceholder: 'Search requests by name',
    nameSearchA11y: 'Search requests by name',
  },
  requestComposer: {
    duplicateBodyMore_other: "“{{title}}” and {{count}} more are still open on this asset. You can still send yours.",
    duplicateBodyMore_one: "“{{title}}” and {{count}} more are still open on this asset. You can still send yours.",
    duplicateBody: "“{{title}}” is still open on this asset. You can still send yours.",
    duplicateSimilar: "A similar request is already open",
    duplicateExact: "This may already be reported",
    summaryMissing: "Add a short summary first, then try again.",
    ai: {
      signInBody: "Your session ended. Sign in again, then add the photo.",
      signInTitle: "Sign in to add photos",
      failedBody: "Try again in a moment, or keep what you wrote.",
      failedTitle: "Could not tidy this up",
      review: "Check it before you use it.",
      titleTitle: "Tidied-up summary",
      detailsTitle: "Tidied-up details",
    },
    titles: {
      default: "Report an issue",
      report: 'Report issue',
      flag: 'Flag problem',
      requestFix: 'Request fix',
      escalate: 'Escalate',
      followUp: "Propose a follow-up",
      rework: "Propose rework",
      quote: "Offer a quote",
    },
    proposal: {
      kindHeading: "What are you proposing?",
      kindFollowUp: "Follow-up",
      kindRework: "Rework",
      kindQuote: "A quote",
      summaryPlaceholder: "e.g. Replace the pressure valve",
      detailsLabel: "What needs doing and why",
      detailsPlaceholder: "What you found, what you would do, and anything they should know",
      submit: "Send proposal",
    },
    close: 'Close',
    assetHeading: 'Asset',
    assetChoose: 'Choose an asset',
    assetSearchPlaceholder: 'Search by asset name…',
    assetTypeAll: 'All types',
    assetNoMatches: 'No assets match your search and filters.',
    dictate: 'Dictate',
    dictationListening: 'Listening…',
    dictationProcessing: 'Inserting transcript…',
    dictationStopping: 'Finishing dictation…',
    dictationHeard: 'Heard: {{text}}',
    // The same words as every other AI rewrite button.
    aiPolish: 'Tidy up with AI',
    contextHeading: 'Context',
    contextReporter: 'Reporter: {{name}}',
    contextInspection: "From an inspection",
    contextJob: "From a job",
    contextSchedule: "From a planned visit",
    outcomeHeading: 'What do you need?',
    outcomeHelp: 'Just help — not sure yet',
    outcomeJob: 'A job done',
    outcomeInspection: 'An inspection',
    summaryLabel: 'Short summary',
    summaryPlaceholder: 'e.g. Failed valve check on line 2',
    detailsLabel: 'What you saw',
    summaryPlaceholderRequester: 'e.g. Kitchen tap dripping',
    detailsLabelRequester: 'What is wrong?',
    detailsPlaceholderRequester: 'What you noticed, where it is, and when it started',
    detailsPlaceholder: 'What you observed, risk, and any constraints',
    priorityLabel: 'Priority',
    priorityNone: 'None',
    priorityPickerTitle: 'Priority',
    proposedDateLabel: 'Preferred timing (optional)',
    proposedDatePlaceholder: 'Select date',
    submit: 'Submit',
    submitRequester: 'Send report',
    submitting: 'Submitting…',
    assetChangeAccessibility: "Change asset",
    assetChange: "Change",
    analyzeEvidence: "Read the photos",
    evidenceAddToDetails: "Add extracted text to details",
  },
  followUpActionModal: {
    stop: "Stop",
    draft: "Draft with AI",
    drafting: "Drafting…",
    polishFailedBody: "Try again in a moment.",
    polishFailedTitle: "Could not tidy up the note",
    draftFailedBody: "Try again, or write it yourself.",
    draftFailedTitle: "Could not draft a question",
    polishReview: "Check the tidied-up note before you use it.",
    draftReview: "Check it before you send it.",
    draftedMessageTitle: "Drafted question",
    polishedNotesTitle: "Tidied-up note",
    phNotes: "Anything they should know, such as when someone will visit.",
    phReject: "Why this will not go ahead. The requester sees this.",
    phClose: "Why is it closing? It is kept on the request.",
    phReplied: "Their answer, as they gave it by email or phone. Kept on the request.",
    phAsk: "What do you need from them? They get it by email or in the app.",
    labelNotes: "Note to the requester (optional, they see this)",
    labelReject: "Reason (the requester sees this)",
    labelClose: "Reason for closing (optional, the requester sees this)",
    labelReplied: "What they said (optional)",
    labelAsk: "Your question (the requester sees this)",
    aiFallbackLabel: "Request",
    loadAssigneesFailed: "Could not load the people you can assign. Try again.",
    searchAssignees: "Search people",
    loadingAssignees: "Loading people…",
    unassigned: "Nobody yet",
    confirmSchedule: "Propose this time",
    confirmClose: "Close request",
    confirmReplied: "Save their reply",
    confirmAsk: "Send question",
    confirmReject: "Decline request",
    confirmApprove: "Approve",
    closeTitle: "Close request",
    repliedTitle: "Log their reply",
    askTitle: "Ask the requester a question",
    approveTitle: 'Approve request',
    rejectTitle: "Decline request",
    scheduleTitle: 'Schedule work',
    convertTitle: 'Choose work path',
    cancel: 'Cancel',
    confirm: 'Confirm',
    saving: 'Saving…',
    targetLabel: 'Outcome type',
    targetJob: 'Tracked job',
    targetInspection: 'Scheduled inspection',
    convertPickerTitle: 'Choose path',
  },
  inspections: {
    screenTitle: 'Inspections',
    templates: "Checklists",
    /**
     * Runner batch labels — the "where am I in this checklist" line.
     *
     * Two registers: the full label for the wide header, and `*Short` variants
     * for the narrow sticky header, where the line has to survive 375px.
     *
     * Counts are separate keys rather than one interpolated sentence because
     * i18next pluralises off a single `count`, and "3 items · 2 groups" carries
     * two independently-plural nouns. Composing them lets each pluralise on its
     * own — which is also what stopped the compact line reading "1 items ·
     * 1 groups" before this existed.
     */
    runner: {
      noItems: 'No items',
      noItemsInGroup: 'No items in this group',
      items_one: '{{count}} item',
      items_other: '{{count}} items',
      groups_one: '{{count}} group',
      groups_other: '{{count}} groups',
      questions_one: '{{count}} question',
      questions_other: '{{count}} questions',
      /** Deliberately abbreviated — the compact line has no room for "questions". */
      questionsShort: '{{count}} Qs',
      groupOf: 'Group {{index}} of {{total}}',
      groupOfShort: 'Group {{index}}/{{total}}',
      itemOf: 'Item {{index}} of {{total}}',
      indexOfShort: '{{index}}/{{total}}',
      inGroup: 'In: {{group}}',
    },
    bulk: {
      select: 'Select inspections',
      selectA11y: 'Select multiple inspections for bulk actions',
      cancelSelection: 'Cancel',
      selected_one: '{{count}} selected',
      selected_other: '{{count}} selected',
      selectAllVisible: 'All visible',
      clearVisible: 'Clear visible',
      selectAllA11y: 'Select all visible inspections',
      deselectAllA11y: 'Deselect all visible inspections',
      deleteInspections: 'Delete',
      deleteConfirmTitle: 'Delete inspections',
      deleteConfirmMessage_one: 'Delete {{count}} inspection? This cannot be undone.',
      deleteConfirmMessage_other: 'Delete {{count}} inspections? This cannot be undone.',
      deleteConfirmAction: 'Delete',
      keep: 'Keep',
      deleteSuccess_one: 'Deleted {{count}} inspection',
      deleteSuccess_other: 'Deleted {{count}} inspections',
      partialResult: '{{succeeded}} done · {{failed}} skipped',
      allFailedTitle: 'Nothing changed',
      allFailedMessage: 'The selected inspections could not be deleted. Please try again.',
    },
    createInspection: 'Create inspection',
    startInspection: 'Start inspection',
    searchA11y: 'Search inspections',
    searchPlaceholderPlanned: 'Search planned inspections?',
    searchPlaceholderDefault: 'Search inspections and reviews?',
    export: 'Export',
    exportCsvA11y: 'Export inspections as CSV',
    refine: 'Refine',
    refineA11y: 'Refine filters and grouping',
    refineSheetTitle: 'Refine inspections',
    refineSearch: 'Search',
    clearFilters: 'Clear filters',
    manageTemplates: "Checklists",
    statusSectionLabel: 'Status',
    bucketTablistLabel: 'Inspection status: All, Needs action, Planned, or History',
    statusHintLine: 'List matches this status.',
    viewToggleToListA11y: 'Switch planned inspections to list view',
    viewToggleToCalendarA11y: 'Switch planned inspections to calendar view',
    buckets: {
      // M3 (IA pass): bucket labels aligned to parallel state form. Previous
      // mix was verb-phrase (Needs action) / adjective (Planned) / noun
      // (History). The new set reads as lifecycle states: pending →
      // scheduled → completed. "Needs action" stays because it's a
      // load-bearing call-to-action label operators already know.
      all: 'All',
      needsAction: 'Needs action',
      planned: 'Scheduled',
      history: 'Completed',
    },
    subtitles: {
      allNone: 'No inspections yet',
      allCount_one: '{{count}} inspection',
      allCount_other: '{{count}} inspections',
      needsActionNone: 'No action required right now',
      needsActionCount_one: '{{count}} item needs attention',
      needsActionCount_other: '{{count}} items need attention',
      plannedNone: 'No planned inspections',
      plannedCount_one: '{{count}} upcoming inspection',
      plannedCount_other: '{{count}} upcoming inspections',
      historyNone: 'No inspection history yet',
      historyCount_one: '{{count}} completed, approved, or reviewed item',
      historyCount_other: '{{count}} completed, approved, or reviewed items',
    },
    emptyFilteredTitle: 'No matching items',
    emptyFilteredSubtitle: 'Change search or clear filters.',
    emptyPlannedTitle: 'No inspections in Planned',
    emptyPlannedSubtitle:
      'Schedule an inspection to show it here.',
    // Calendar view only plots inspections that have a scheduled date. When it's
    // empty the other views (Cards/List/Kanban) may still have items, so the copy
    // explains that rather than implying there are no inspections at all.
    emptyCalendarTitle: 'No scheduled inspections',
    emptyCalendarSubtitle:
      'The calendar shows inspections with a scheduled date. Switch to Cards or List to see all inspections here.',
    emptyHistoryTitle: 'No inspections in History',
    emptyHistorySubtitle: 'Completed inspections appear here.',
    emptyAllTitle: 'No inspections yet',
    emptyAllSubtitle: 'Inspections, reviews, and schedules appear here.',
    // Needs action is the landing bucket, so this is what a caught-up user sees
    // first. It has to read as "you are done", not as an empty container.
    emptyNeedsActionTitle: 'Nothing needs you right now',
    emptyNeedsActionSubtitle: 'Inspections appear here when they need action.',
    followUp: {
      logFromItem: 'Log from checklist item',
    },
    refineScope: 'Scope',
    refineGroupBy: 'Group by',
    refineGroupByHint: 'Select one or more to nest groups',
    refineFilters: 'Filters',
    refineInspectionStage: 'Inspection stage',
    refineScheduleStage: 'Schedule stage',
    filterChipAsset: 'Asset',
    filterChipTemplate: 'Checklist',
    filterChipInspector: 'Inspector',
    groupByAsset: 'Asset',
    groupByInspector: 'Inspector',
    groupByTemplateCategory: 'Checklist category',
    groupingUnassigned: 'Unassigned',
    groupingUncategorized: 'Uncategorized',
    inspectorAll: 'All',
    inspectorMe: 'Me',
    inspectionStages: {
      all: 'All inspection stages',
      allShort: 'All stages',
      draft: 'Draft',
      inProgress: 'In progress',
      completed: 'Completed',
      approved: 'Approved',
      cancelled: 'Cancelled',
    },
    scheduleStages: {
      all: 'All schedule stages',
      allShort: 'All',
      upcoming: 'Upcoming',
      due: 'Due',
      overdue: 'Overdue',
    },
    reviewStages: {
      pending: 'Review pending',
      responded: 'Review responded',
      awaitingReply: 'Shared — awaiting reply',
      awaitingVerification: 'Awaiting verification',
      /** A live row with nothing else to say shows its column. */
      column: {
        triage: 'Open',
        scheduled: 'Scheduled',
        in_progress: 'In progress',
        awaiting_review: 'Awaiting review',
      },
    },
    kanban: {
      dueAndOverdue: 'Due & overdue',
    },
    workflowStage: {
      draft: 'Draft',
      in_progress: 'In progress',
      completed: 'Completed',
      approved: 'Approved',
      cancelled: 'Cancelled',
    },
    pickerAllTemplates: 'All checklists',
    pickerTitleAsset: 'Asset',
    pickerTitleTemplate: 'Checklist',
    pickerTitleInspector: 'Inspector',
    reviewFallbackReport: 'Inspection report',
    reviewFallbackAsset: 'Asset',
    reviewStatusApproved: 'Approved',
    reviewStatusDeclined: 'Declined',
    reviewStatusPending: 'Pending review',
    resultsPanelA11y: 'Inspection results for the selected bucket',
    fabCreateA11y: 'Create inspection',
    exportCsv: {
      id: 'Id',
      template: 'Checklist',
      asset: 'Asset',
      status: 'Status',
      stage: 'Stage',
      assignee: 'Assignee',
      lastActivity: 'Last activity',
    },
    operationalInsight: {
      title: 'Inspection register insight',
      sourceLabel: 'Existing inspections, schedules, and reviews',
      summaryLine:
        '{{bucketNeedsAction}} need attention · {{planned}} scheduled · {{history}} completed for this scope',
      overdueSchedExplanation_one: '{{count}} scheduled inspection is overdue.',
      overdueSchedExplanation_other: '{{count}} scheduled inspections are overdue.',
      overdueSchedRecommended: 'Start or reschedule them so coverage and compliance stay on track.',
      overdueSchedCta: 'Show overdue schedules',
      pendingReviewExplanation_one: '{{count}} shared report is waiting on a reviewer response.',
      pendingReviewExplanation_other: '{{count}} shared reports are waiting on a reviewer response.',
      pendingReviewRecommended: 'Respond or nudge reviewers so approvals are not blocked.',
      pendingReviewCta: 'Open next review',
      dueSoonExplanation_one: '{{count}} scheduled inspection is due soon.',
      dueSoonExplanation_other: '{{count}} scheduled inspections are due soon.',
      dueSoonRecommended: 'Confirm who will run them and capture any prep work.',
      dueSoonCta: 'Show due schedules',
      inProgressExplanation_one: '{{count}} inspection is in draft or in progress.',
      inProgressExplanation_other: '{{count}} inspections are in draft or in progress.',
      inProgressRecommended: 'Continue checklists, capture evidence, and hand off if needed.',
      inProgressCta: 'Focus in-progress',
      healthyExplanation: 'No pending reviews or overdue schedules in this filtered view.',
      healthyRecommended: 'Use refine to change scope, grouping, or search when you need a narrower list.',
      refineCta: 'Open refine',
      exportCsvLabel: 'Export inspections (CSV)',
      wideCollapsedLead: 'Expand for actions.',
      widePanelLead: 'Actions match this view.',
    },
    tableHeaderWorkItem: 'Work item',
    tableHeaderStatus: 'Status',
    tableHeaderAssigned: 'Assigned',
    tableHeaderDueDate: 'Due date',
  },
  templates: {
    addIndustryPack: "Add an industry pack",
    emptyNoneSubtitlePacks: "Add the ready-made checklists for your trade, or create your own.",
    screenTitle: 'Checklists',
    createManually: 'Create manually',
    createManuallyA11y: 'Create manually',
    refineScope: 'Scope',
    subtitleCount_one: '{{count}} checklist',
    subtitleCount_other: '{{count}} checklists',
    fromPack_one: '{{count}} from the {{pack}} pack',
    fromPack_other: '{{count}} from the {{pack}} pack',
    inspectionsLink: 'Inspections',
    searchPlaceholder: 'Search checklists...',
    searchPlaceholderWeb: 'Search checklists, categories, or descriptions',
    searchA11y: 'Search checklists',
    refine: 'Refine',
    refineSheetTitle: 'Refine checklists',
    refineGroupBy: 'Group by',
    refineGroupByHint: 'Select one or more to nest groups',
    refineFilters: 'Filters',
    filterAssetType: 'Asset type',
    filterCategory: 'Category',
    groupByAssetType: 'Asset type',
    groupByCategory: 'Category',
    pickerTitleAssetType: 'Asset type',
    pickerTitleCategory: 'Category',
    allTypes: 'All types',
    allCategories: 'All categories',
    statusSectionLabel: 'Status',
    bucketTablistLabel: 'Checklist status: Needs action, Planned, or History',
    statusHintLine:
      'Needs action, planned, and history.',
    refineA11y: 'Refine checklist filters',
    clear: 'Clear',
    clearA11y: 'Clear search and filters',
    generateWithAi: 'Generate with AI',
    createChooserTitle: 'Create checklist',
    createChooserSubtitle: "Choose how to build your checklist.",
    newTemplate: 'New checklist',
    createTemplate: 'Create checklist',
    createTemplateA11y: 'Create checklist',
    newTemplateA11y: 'New checklist',
    emptyNoneTitle: "No checklists yet",
    emptyNoneSubtitle: 'Create a checklist.',
    emptyFilteredTitle: 'No matching checklists',
    emptyFilteredSub: 'Try different search terms or clear filters.',
    clearFilters: 'Clear filters',
    emptyBucketTitle: 'No checklists in {{bucket}}',
    emptyBucketSub:
      'Switch bucket or clear filters.',
    goToNeedsAction: 'Go to Needs action',
    goToNeedsActionA11y: 'Show checklists in Needs action',
    inspectorHeading: 'Inspector',
    compatibility: 'Compatibility',
    versioning: 'Versioning',
    structure: 'Structure',
    visibility: 'Visibility',
    description: 'Description',
    versioningCurrentId: 'Current version ID: {{id}}',
    versioningNotSet: 'Not set',
    lastUpdatedPrefix: 'Last updated',
    structureLine:
      '{{groups}} groups / {{items}} items / {{conditional}} conditional rules',
    requiredChecks_one: '{{count}} required check',
    requiredChecks_other: '{{count}} required checks',
    visibilityAllTeams: 'All teams',
    visibilitySelectedTeams: 'Selected teams',
    openTemplate: 'Open checklist',
    edit: 'Edit',
    useForInspection: 'Start inspection',
    detail: {
      duplicate: 'Duplicate',
      delete: 'Delete',
      sharedBadge: 'Shared',
      notFound: 'Checklist not found',
      restrictToSelectedTeams: 'Restrict to selected teams',
      shareWithAllTeams: 'Share with all teams',
      visibleToAllTeams: 'Visible to all teams',
      deleteConfirmTitle: 'Delete checklist',
      deleteConfirmBody: 'Delete “{{name}}”? Past inspections that used it will keep their record.',
      cancel: 'Cancel',
      duplicatedTitle: 'Duplicated',
      duplicatedBody: 'A copy of this checklist has been created.',
      errorTitle: 'Something went wrong',
      failedToDelete: 'Could not delete this checklist. Please try again.',
      failedToDuplicate: 'Could not duplicate this checklist. Please try again.',
      failedToUpdate: 'Could not update this checklist. Please try again.',
      usedByTitle: 'Used in',
      usedByCount_one: '{{count}} inspection',
      usedByCount_other: '{{count}} inspections',
      usedByEmpty: 'No inspections have used this checklist yet.',
      usedByOpenInspection: 'Open inspection',
    },
    selectTemplateTitle: 'Select a checklist',
    selectTemplateSub: 'Pick a row to review its structure, versions, and usage actions.',
    groupingUncategorized: 'Uncategorized',
    labelGeneral: 'General',
    labelAllAssets: 'All assets',
    fabNewTemplateA11y: 'New checklist',
    buckets: {
      all: 'All',
      needsAction: 'Needs action',
      planned: 'Planned',
      history: 'History',
    },
  },
  jobs: {
    followUp: {
      requestFix: 'Request fix',
      escalate: 'Escalate',
      logForTriage: 'Log follow-up',
      logForTriageHint: 'Opens this asset’s follow-ups with job context for Approvals.',
    },
    operationalInsight: {
      summaryEmpty: 'No jobs match the current scope and filters.',
      summaryCounts:
        '{{total}} jobs in this view · {{overdue}} overdue · {{unassigned}} unassigned · {{unscheduled}} without a scheduled start',
      overdueExplanation_one: '{{count}} job is past its scheduled start or due date.',
      overdueExplanation_other: '{{count}} jobs are past their scheduled start or due date.',
      overdueRecommended: 'Confirm ownership, reschedule, or close out work that is already done.',
      overdueCta: 'Show overdue',
      unassignedExplanation_one: '{{count}} open job has no assignee.',
      unassignedExplanation_other: '{{count}} open jobs have no assignee.',
      unassignedRecommended: 'Assign each job so owners, customers, and contractors know who drives the work.',
      unassignedCta: 'Show unassigned',
      unscheduledExplanation_one: '{{count}} accepted job still needs a scheduled start.',
      unscheduledExplanation_other: '{{count}} accepted jobs still need a scheduled start.',
      unscheduledRecommended: 'Set a start window before work slips or conflicts with other commitments.',
      unscheduledCta: 'Show unscheduled',
      urgentExplanation_one: '{{count}} job is marked high urgency.',
      urgentExplanation_other: '{{count}} jobs are marked high urgency.',
      urgentRecommended: 'Scan for blockers, missing evidence, and customer-visible delays.',
      urgentCta: 'Filter high urgency',
      healthyExplanation: 'No overdue, unassigned, or unscheduled jobs in this filtered view.',
      healthyRecommended: 'Keep dates, assignees, and updates current as work moves.',
      healthyCta: 'Refine filters',
      exportCsvLabel: 'Export visible jobs (CSV)',
      wideCollapsedLead: 'Expand for actions.',
      widePanelLead: 'Actions match this view.',
    },
    screenTitle: 'Jobs',
    createJob: 'Create job',
    createJobA11y: 'Create job',
    searchPlaceholder: 'Search jobs...',
    searchPlaceholderWeb: 'Search jobs, assets, assignees...',
    searchA11y: 'Search jobs',
    refine: 'Refine',
    refineSheetTitle: 'Refine jobs',
    refineSearch: 'Search',
    refineJobsA11y: 'Refine job filters',
    refineScope: 'Scope',
    refineGroupBy: 'Group by',
    refineGroupByHint: 'Select one or more to nest groups',
    refineFilters: 'Filters',
    refineStage: 'Stage',
    refineQuoteWorkflow: 'Quote workflow',
    clearFilters: 'Clear filters',
    advancedFiltersTitle: 'Advanced filters & sorting',
    advancedFiltersSummary_one: '{{count}} refinement active',
    advancedFiltersSummary_other: '{{count}} refinements active',
    advancedFiltersSummaryEmpty: 'Group by, filters, stage, scope, sort',
    advancedFiltersExpand: 'Show filters',
    advancedFiltersCollapse: 'Hide filters',
    exportFilteredCsvA11y: 'Export currently filtered jobs as CSV',
    statusSectionLabel: 'Status',
    bucketTablistLabel: 'Job status: All, Needs action, Planned, or History',
    statusHintLine: 'Queue matches this bucket.',
    switchToListA11y: 'Switch jobs to list view',
    switchToCalendarA11y: 'Switch jobs to calendar view',
    bucketsNative: {
      all: 'All',
      needs_action: 'Needs action',
      scheduled: 'Scheduled',
      closed: 'Closed',
    },
    bucketsWeb: {
      all: 'All',
      needs_action: 'Needs action',
      scheduled: 'Planned',
      closed: 'History',
    },
    sort: {
      newest: 'Newest first',
      oldest: 'Oldest first',
      name_az: 'Name A-Z',
      name_za: 'Name Z-A',
      asset_name: 'Asset name',
      highest_cost: 'Highest cost',
      lowest_cost: 'Lowest cost',
      assignee: 'Assignee',
    },
    sortBy: 'Sort by',
    groupBy: {
      asset: 'Asset',
      status: 'Status',
    },
    attention: {
      all: 'All',
      overdue: 'Overdue',
      unscheduled: 'Unscheduled',
    },
    webQueue: {
      all: 'All active',
      overdue: 'Overdue',
      awaiting_acceptance: 'Awaiting acceptance',
      unscheduled: 'Unscheduled',
      in_progress: 'In progress',
      due_soon: 'Due soon',
      other: 'Scheduled later',
    },
    quoteState: {
      all: 'All quote states',
      awaiting_request: 'Awaiting quote request',
      requested: 'Quote request linked',
      awarded: 'Quote awarded',
    },
    quoteLinked: 'Linked',
    quoteNone: 'None',
    priority: {
      all: 'All',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      critical: 'Critical',
    },
    deliveryAll: 'All',
    deliveryMode: {
      INTERNAL: 'Internal',
      DIRECT_CONTRACTOR: 'Direct contractor',
      PRIVATE_QUOTES: 'Private quotes',
    },
    categoryAll: 'All categories',
    assigneeAll: 'All assignees',
    assigneeUnassigned: 'Unassigned',
    assigneeAssignedFallback: 'Assigned',
    filterChip: {
      asset: 'Asset',
      category: 'Category',
      workPath: 'Work path',
      priority: 'Priority',
      attention: 'Attention',
      sort: 'Sort',
      assignee: 'Assignee',
      quoteState: 'Quote state',
    },
    pickerTitle: {
      asset: 'Asset',
      category: 'Category',
      workPath: 'Work path',
      priority: 'Priority',
      sortBy: 'Sort by',
      attention: 'Attention',
      assignee: 'Assignee',
      quoteState: 'Quote state',
    },
    stages: {
      all: {
        all: 'All',
      },
      needs_action: {
        all: 'All',
        draft: 'Draft',
        open: 'Open',
        accepted: 'Accepted',
        completed: 'Completed',
        rejected: 'Rejected',
      },
      scheduled: {
        all: 'All',
        scheduled: 'Scheduled',
        in_progress: 'In progress',
        paused: 'Paused',
      },
      closed: {
        all: 'All',
        verified: 'Verified',
        cancelled: 'Cancelled',
      },
    },
    grouping: {
      noAsset: 'No asset',
      noSkill: 'No skill',
      unassigned: 'Unassigned',
      uncategorized: 'Uncategorized',
    },
    analytics: {
      assigneeChannel: {
        unassigned: 'Unassigned',
        team_member: 'Team member',
        contractor: 'Contractor',
        type_unset: 'Assignee (type unset)',
      },
      privateQuotesPipeline: {
        awaitingRequest: 'No quote request yet',
        requested: 'Awaiting award',
        awarded: 'Quote awarded (in flight)',
      },
      notSet: 'Not set',
    },
    urgency: {
      normal: 'Normal',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
    },
    relative: {
      justNow: 'Just now',
      minutesAgo: '{{count}}m ago',
      hoursAgo: '{{count}}h ago',
    },
    sortDash: '-',
    alerts: {
      cancelTitle: 'Cancel job',
      cancelMessage: 'Are you sure? The job will be marked as cancelled.',
      keep: 'Keep',
      cancelConfirm: 'Cancel job',
      deleteTitle: 'Delete job',
      deleteMessage: 'This cannot be undone. The job will be permanently removed.',
      deleteConfirm: 'Delete',
      errorTitle: 'Error',
      failedCancel: 'Failed to cancel',
      failedDelete: 'Failed to delete',
      acceptFailedTitle: 'Unable to accept job',
      acceptFailedMessage: 'Please try again.',
    },
    bulk: {
      select: 'Select jobs',
      selectA11y: 'Select multiple jobs for bulk actions',
      cancelSelection: 'Cancel',
      selected_one: '{{count}} selected',
      selected_other: '{{count}} selected',
      selectAllVisible: 'All visible',
      clearVisible: 'Clear visible',
      selectAllA11y: 'Select all visible jobs',
      deselectAllA11y: 'Deselect all visible jobs',
      cancelJobs: 'Cancel',
      deleteJobs: 'Delete',
      cancelConfirmTitle: 'Cancel jobs',
      cancelConfirmMessage_one: 'Cancel {{count}} job? It will be marked as cancelled.',
      cancelConfirmMessage_other: 'Cancel {{count}} jobs? They will be marked as cancelled.',
      cancelConfirmAction: 'Cancel jobs',
      deleteConfirmTitle: 'Delete jobs',
      deleteConfirmMessage_one: 'Delete {{count}} job? This cannot be undone.',
      deleteConfirmMessage_other: 'Delete {{count}} jobs? This cannot be undone.',
      deleteConfirmAction: 'Delete',
      keep: 'Keep',
      noneCancellable: 'None of the selected jobs can be cancelled.',
      cancelSuccess_one: 'Cancelled {{count}} job',
      cancelSuccess_other: 'Cancelled {{count}} jobs',
      deleteSuccess_one: 'Deleted {{count}} job',
      deleteSuccess_other: 'Deleted {{count}} jobs',
      partialResult: '{{succeeded}} done · {{failed}} skipped',
      allFailedTitle: 'Nothing changed',
      allFailedMessage: 'The selected jobs could not be updated. Please try again.',
    },
    emptyNoJobs: 'No jobs',
    emptyNoMatching: 'No matching jobs',
    emptyFilteredHint: 'Try different search terms or clear filters.',
    subtitleAttentionSummary: '{{overdue}} overdue · {{unscheduled}} unscheduled',
    webSubtitleSummary:
      '{{total}} jobs · {{overdue}} overdue · {{inProgress}} in progress · {{dueSoon}} due soon',
    noAssetLinked: 'No asset linked',
    metaAsset: 'Asset',
    metaPriority: 'Priority',
    overview: {
      queue: 'Queue',
      status: 'Status',
      assignee: 'Assignee',
      workPath: 'Work path',
      schedule: 'Schedule',
      nextStep: 'Next step',
      started: 'Started',
      completed: 'Completed',
      duration: 'Duration',
      quoteState: 'Quote state',
      summary: 'Summary',
      inspection: 'Inspection',
    },
    notSet: 'not set',
    emDash: '—',
    nextStepFallback: 'Review in full details',
    workflowNextStep: {
      awaitingFollowOn: 'Awaiting follow-on approval',
      draft: 'Review details and open job',
      openAwaitingAcceptance: 'Accept or decline job',
      openSchedule: 'Schedule job',
      openAssign: 'Assign job',
      rejected: 'Review rejection and reassign',
      scheduled: 'Start job',
      inProgress: 'Complete or pause',
      inProgressRework: 'Resubmit completion',
      paused: 'Resume or reschedule',
      completed: 'Verify or request rework',
    },
    verifyCompletion: {
      title: 'Sign off the job',
      message: 'Sign this job off? The person who did it will be told.',
      confirm: 'Sign off',
    },
    video: {
      unsupportedSource: 'This video cannot be played from this location.',
      loadFailed: 'Video could not be loaded.',
    },
    notScheduled: 'Not scheduled',
    openInspectionA11y: 'Open inspection {{label}}',
    media: {
      closeImageViewer: 'Close image viewer',
      closeVideoViewer: 'Close video player',
    },
    kpi: {
      overdueLabel: 'Overdue',
      overdueSub: 'Needs immediate action',
      inProgressLabel: 'In progress',
      inProgressSub: 'Currently being executed',
      dueSoonLabel: 'Due soon',
      dueSoonSub: 'Next 72 hours',
    },
    command: {
      openCalendar: 'Open calendar',
      createJob: 'Create job',
      refine: 'Refine',
      clearFilters: 'Clear filters',
      exportCsv: 'Export CSV',
    },
    exportCsvHeaders: {
      title: 'Title',
      status: 'Status',
      asset: 'Asset',
      priority: 'Priority',
      assignee: 'Assignee',
      updated: 'Updated',
    },
    inspector: {
      openJob: 'Open job',
      addUpdate: 'Add update',
      accept: 'Accept',
      reject: 'Reject',
      assign: 'Assign',
      schedule: 'Schedule',
      start: 'Start',
      pause: 'Pause',
      resume: 'Resume',
      complete: 'Complete',
      approve_rework: 'Approve rework',
      verify: 'Verify',
      rework: 'Rework',
    },
    workQueueHeading: 'Work queue',
    queueEmpty: 'No jobs in this queue.',
    emptyMatchingWebTitle: 'No matching jobs',
    emptyMatchingWebSub: 'Try another queue or clear filters.',
    emptyBucketTitle: 'No jobs in {{bucket}}',
    emptyBucketSub: 'Switch to Needs action, Planned, or History, or clear filters to widen results.',
    goToNeedsAction: 'Go to Needs action',
    goToNeedsActionA11y: 'Show Needs action jobs',
    clearJobFiltersA11y: 'Clear job filters',
    panelHeading: 'Job details',
    emptyScopeTitle: 'No jobs in scope',
    emptyScopeSub: 'Adjust your filters, queue, or team scope to load jobs.',
    sectionOverview: 'Overview',
    sectionActivity: 'Activity',
    noActivityYet: 'No activity yet.',
    loading: 'Loading jobs…',
    emptyNoJobsYet: 'No jobs yet',
    emptyRecipientSub:
      'Jobs from inspections shared with you will appear here once the inspector creates them from approved repairs.',
    emptyOwnerSub: 'Create a job directly or convert approved repairs from a shared inspection.',
    viewActivity: 'View Activity',
    canonicalStatus: {
      skipped: "Skipped",
      draft: 'Draft',
      open: 'Open',
      accepted: 'Accepted',
      scheduled: 'Scheduled',
      in_progress: 'In progress',
      paused: 'Paused',
      completed: 'Completed',
      verified: 'Signed off',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
    },
    fabCreateA11y: 'Create job',
    detailWorkPath: {
      change: 'Change work path',
      sheetTitle: 'Work path',
      sheetHint: 'Choose how this job is delivered: internal team, one contractor, or request quotes.',
      team: 'Team',
      contractor: 'Contractor',
      quotes: 'Quotes',
    },
  },
  inspectionDetail: {
    firstRunMomentumHint: "Answer each item with Pass, Fail or N/A. Tap Follow-up on anything that needs a job.",
    firstRunGotIt: 'OK',
    firstRunReportCta: 'Report issue',
    reportIssueNow: 'Report issue now',
    completeInspection: 'Complete inspection',
    completeShort: 'Complete',
    continueInspection: 'Continue inspection',
    continueShort: 'Continue',
    manage: 'Manage',
    manageSheetTitle: 'Inspection actions',
    reassign: 'Reassign',
    pause: 'Pause',
    cancel: "Cancel inspection…",
    viewReport: 'View report',
    nextSteps: 'Next steps',
    share: 'Share',
    createJobs: 'Create jobs',
    sendForApproval: 'Send for approval',
    shareReport: 'Share report',
    planFollowUp: 'Plan follow-up',
    planFollowUpHint: 'Optional steps after you finish this inspection.',
    saveTemplate: 'Save checklist',
    repeatOnSchedule: 'Repeat on schedule',
    createJobNow: 'Create job now',
    notifyTeam: 'Notify team',
    moreMenuA11y: 'More inspection actions',
  },
  assetDetail: {
    shareChooser: {
      intakeDescription: "No account or sign-in. Each report arrives in Work as a request, ready for you to plan an inspection or a job.",
      intakeTitle: "Let anyone with the code report a problem",
      personDescription: "They see this asset, its inspections and documents. Choose Can request work on the next step if they should report problems too.",
      personTitle: "Share with a person or team",
    },
    publicIntakeCard: {
      turnOff: "Turn off",
      keepOn: "Keep it on",
      turnOffBody: "Printed codes and shared links stop working until you turn it back on.",
      turnOffTitle: "Turn off reporting?",
      copyFailedBody: "Try again. You can also take a screenshot of the code and share that.",
      copyFailedTitle: "Could not copy the link",
      linkCopied: "Link copied. Paste it wherever people should report problems.",
      tryAgain: "Check your connection and try again.",
      updateFailedTitle: "Reporting was not changed",
      turnedOff: "Reporting is off. The code and link no longer accept reports.",
      turnedOn: "Reporting is on. Print the code or copy the link below and put it where people will see it.",
      printBody: "Scan to report a problem. No account or sign-in needed.",
      printTitle: "Report a problem — {{name}}",
      popupBlockedBody: "Allow pop-ups for this site to print, or use Copy link instead.",
      popupBlockedTitle: "Pop-up blocked",
      noWebAddress: "Reporting is on, but this version of the app cannot make the link. Open this asset in AssetWRX in a web browser to copy the link or print the code.",
      copyLinkA11y: "Copy the link for reporting a problem",
      step3: "3. They describe the problem. It appears in Work as a request for you to look at.",
      qrLabel: "Code for reporting a problem",
      title: "Let anyone report a problem",
    },
    overviewInspectionEmpty: 'No inspections yet. Use',
    startInspection: 'Start inspection',
    createJob: 'Create job',
    edit: 'Edit',
    contactsSectionTitle: 'Linked contacts',
    viewActivity: 'View activity',
    toolsAi: 'Tools & AI',
    generateAdvancedReport: "Asset 360",
    askAi: 'Ask AI',
    suggestNextSteps: 'Suggest next steps',
    shareThisAssetModalTitle: 'Share this asset',
    shareThisAssetAccessOnlySubtitle: "Give a person or team access to this asset's record, inspections and documents.",
    shareWithPersonAccessOnlyDescription: "They see this asset's record, inspections and documents.",
    linkContactModalTitle: 'Link contact',
    shareWithContactCallout: 'Share this asset with {{name}}',
    journey: {
      title: 'Operational Journey',
      stage: {
        created: 'Created',
        inspected: 'Inspections complete',
        issues: 'Issues recorded',
        requests: 'Requests raised',
        jobs: 'Jobs complete',
        reports: 'Reports generated',
      },
      window: {
        '30d': '30d',
        '90d': '90d',
        '365d': '1y',
        all: 'All',
      },
      windowLabel: {
        '30d': 'Over the last 30 days',
        '90d': 'Over the last 90 days',
        '365d': 'Over the last 12 months',
        all: 'Across this asset’s full history',
      },
      readOnlyHint: 'You have read-only access to this asset’s journey.',
      a11ySummaryLabel: 'Operational journey summary',
      a11yStageHint: '{{label}}: {{count}} in window',
      a11yStageSubline: '{{label}}: {{count}} in window, {{subline}}',
      zeroInWindow: '0 in {{window}}',
      dateUnavailable: 'Date unavailable',
      ago: {
        minutes: '{{n}}m ago',
        hours: '{{n}}h ago',
        days: '{{n}}d ago',
        months_one: '{{count}}mo ago',
        months_other: '{{count}}mo ago',
        years_one: '{{count}}y ago',
        years_other: '{{count}}y ago',
      },
      in: {
        minutes: 'in {{n}}m',
        hours: 'in {{n}}h',
        days: 'in {{n}}d',
      },
      sublineIssueBreach: '{{days}}d+ without a request',
      sublineAwaitingApproval_one: '{{count}} awaiting approval',
      sublineAwaitingApproval_other: '{{count}} awaiting approval',
      /** components/asset/journey/buildJourneyData.ts — the summary under the strip. */
      summary: {
        noActivity: '{{window}} this asset has no recorded activity.',
        recommendBaseline: 'Recommend scheduling a baseline inspection.',
        hadBoth: '{{window}} this asset has had {{inspections}} and {{jobs}}.',
        hadOne: '{{window}} this asset has had {{what}}.',
        hadNone: '{{window}} this asset has had no completed work yet.',
        inspections_one: '{{count}} inspection',
        inspections_other: '{{count}} inspections',
        jobsCompleted_one: '{{count}} job completed',
        jobsCompleted_other: '{{count}} jobs completed',
        healthImproved: 'Operational health improved from {{then}}/100 to {{now}}/100.',
        healthDeclined: 'Operational health declined from {{then}}/100 to {{now}}/100.',
        healthCurrent: 'Current operational health is {{now}}/100, based on compliance, schedules, jobs, approvals, and inspection findings.',
        conditionUnscored_one: 'Inspection condition is not scored: none of the {{count}} completed inspection has a valid 1–5 rating.',
        conditionUnscored_other: 'Inspection condition is not scored: none of the {{count}} completed inspections have a valid 1–5 rating.',
        conditionOneRated: 'Inspection condition has one rated inspection; one more is needed to show a trend.',
        issueOverdue: 'One open issue is overdue for a request.',
        requestsAwaiting_one: 'One request is awaiting approval.',
        requestsAwaiting_other: '{{count}} requests are awaiting approval.',
        excluded_one: '{{count}} completed inspection was excluded: {{reasons}}.',
        excluded_other: '{{count}} completed inspections were excluded: {{reasons}}.',
        excludedMissing: '{{n}} missing a completion date',
        excludedInvalid: '{{n}} with an invalid completion date',
        excludedFuture: '{{n}} dated in the future',
      },
    },
  },
  quickComplete: {
    moreOptions: 'Advanced completion wizard',
    moreOptionsA11y: 'Open advanced completion wizard',
    eyebrow: "Quick complete",
    needsMore: {
      invoice: "This job also needs its costs and invoice. Continue adds them, then completes it.",
      meter: "This job also needs a meter reading. Continue asks for it, then completes the job.",
      checks: "Something needs clearing before this job can be completed. Continue shows what.",
    },
    onePageTitle: "Complete the job",
    continue: "Continue",
    submit: "Complete job",
    step1: {
      title: "Quick note",
      noteLabel: "What did you do?",
      notePlaceholder: "One line — fixed, replaced, inspected, cleaned.",
      noteA11y: "Quick completion note",
      dictateStart: "Dictate",
      dictateStop: "Stop dictation",
      chipsLabel: "Or tap a quick tag",
    },
    step2: {
      title: "Add evidence",
      summary: "Snap a photo or attach a document. Optional, but recommended.",
      unavailable: "Inline evidence is unavailable here.",
    },
    step3: {
      title: "Submit",
      summary: "Confirm and complete.",
      note: "Note",
      noNote: "No note added",
      tags: "Tags",
      evidence: "Evidence",
      outcome: "Outcome",
    },
  },
  // "Can't complete as scheduled" — the field agent's one honest exit. Never
  // the word "exception": the technical model stays internal.
  changeRequest: {
    comeBack: {
      title: "I need to come back",
      hint: "Another visit on another day. The job stays open and is completed once, at the end.",
    },
    open: "Can't complete as scheduled",
    openA11y: "Report that you can't complete this as scheduled",
    eyebrow: 'Change requested',
    step1: {
      title: 'What stopped you?',
      summary: 'Pick the closest reason. It decides who needs to know.',
    },
    step2: {
      datePlaceholder: "Pick a day",
      title: 'What do you need?',
      summary: 'Your manager decides. Nothing changes until they do.',
      summaryImmediate: 'Skipping applies straight away on this plan. The rest goes to your manager.',
      dateLabel: 'Move it to',
      assigneeLabel: 'Suggest someone',
      assigneeHint: 'A suggestion — your manager may pick someone else.',
    },
    step3: {
      title: 'Anything else?',
      noteLabel: 'Add detail',
      notePlaceholder: 'What you saw, who you spoke to, what would unblock it.',
      evidencePrompt: 'A photo makes this much easier to approve.',
      submit: 'Send request',
      submitImmediate: 'Skip this one',
      submitting: 'Sending…',
    },
    reason: {
      no_access: 'No access',
      asset_unavailable: 'Asset unavailable',
      asset_in_use: 'Asset in use',
      unsafe: 'Unsafe to proceed',
      parts_unavailable: 'Parts unavailable',
      time_insufficient: 'Not enough time',
      meter_inaccessible: "Can't reach the meter",
      specialist_required: 'Specialist required',
      wrong_assignee: 'Should be someone else',
      done_elsewhere: 'Already done elsewhere',
      not_required: 'Not required',
      bigger_than_described: 'Bigger than described',
      other: 'Something else',
    },
    action: {
      reschedule: 'Move it',
      rescheduleBlurb: 'Same work, same person, a later date.',
      rescope: "Needs more than described",
      rescopeBlurb: "Same work, same person, but bigger than described. Say how long it really needs.",
      rescopeLength: "How long does it really need? (minutes)",
      reassign: 'Someone else',
      reassignBlurb: 'Same work, still due, a different person.',
      skip: 'Skip this one',
      skipBlurb: "This visit will not be done. The next one is still due.",
    },
    status: {
      waitingOnApprover: "Waiting for the asset's approver to decide. You will get a notification.",
      pending: 'Change requested',
      waitingOn: "Waiting for {{name}} to decide. You will get a notification.",
      approved: 'Change approved',
      rejected: 'Change not approved',
      stillDue: 'This work is still due',
    },
    review: {
      closeStale: "Close request",
      changedSinceDecide: "This work changed after the request was sent, so it can no longer be approved. Close it with a note; they can ask again if they still need to.",
      title: 'Review request',
      requestedBy: 'Requested by {{name}}',
      asked: 'Asked for',
      reason: 'Reason',
      evidence: 'Evidence',
      current: 'Currently',
      proposed: 'Proposed',
      changedSince: 'This work changed since the request was raised.',
      approve: 'Approve',
      reject: 'Reject',
      editAndApprove: 'Change and approve',
      rejectNeedsNote: 'Say why before rejecting',
      changeDate: 'Approve a different date',
      changeAssignee: 'Approve someone else',
      note: 'Add a note',
      complianceWarning: 'This asset carries a compliance duty.',
    },
  },
  completeWizard: {
    needs: {
      title: "Checks and evidence",
      summary: "Tick the checklist and add the photos. The photos are checked against the job for you.",
    },
    eyebrow: 'Complete job',
addCosts: 'Add costs & invoice',
    submit: 'Submit completion',
    step1: {
      title: "Notes",
      noteLabel: 'What did you do?',
      notePlaceholder: 'Short summary — what you fixed, replaced, inspected.',
      noteA11y: 'Completion note',
      chipsLabel: 'Quick tags',
      dictate: 'Dictate',
      dictateStart: 'Dictate',
      dictateStop: 'Stop dictation',
      improve: 'Clean up with AI',
      improving: 'Cleaning…',
    },
    step2: {
      title: 'Evidence',
      summary: 'Add proof of the work. Photos, documents, or an invoice if needed.',
      addPhoto: 'Add photo',
      addDocument: 'Add document',
      addInvoice: 'Add invoice',
      // Pluralised properly — both completion surfaces used to render the
      // literal "1 item(s) attached".
      count_one: '{{count}} item attached',
      count_other: '{{count}} items attached',
      empty: 'No evidence attached yet.',
      analyze: "Read the photos",
      addToNote: "Add extracted text to note",
    },
    step3: {
      title: "Costs & invoice",
      summary: "Record what this job cost. Add an invoice or receipt if you have one.",
      summaryInvoice: "Record what this job cost and attach an invoice. This client requires one.",
      empty: "No cost editor available here. Use the manage view to record costs.",
    },
    // These two blocks were off by one. The block below was keyed `step4` but
    // held step FIVE's content ("Review and submit"), while step four's own
    // strings had no entry at all. So step 4 rendered "Review and submit" —
    // the wrong label on the wrong step, in every locale — and step 5 fell
    // through to its hardcoded English default, also in every locale. Neither
    // failed loudly, because `tt(key, fallback)` always has English to hand.
    step4: {
      title: 'Outcome',
      summary: 'How did this job end?',
      followUpHint: "We'll offer to create a follow-up job after you submit.",
    },
    step5: {
      title: 'Review and submit',
      summary: 'Confirm what gets submitted.',
      note: 'Note',
      noNote: 'No note added',
      tags: 'Tags',
      evidence: 'Evidence',
      outcome: 'Outcome',
      cost: 'Cost',
      noCost: 'Not recorded',
      invoice: 'Invoice',
      invoiceNone: 'None',
      evidenceNone: 'None',
      blockersTitle: 'Fix before submitting:',
      invoiceAttached: "{{n}} attached",
      invoiceRequiredMissing: "Required — none attached",
      invoiceRequiredBlocker: "This client requires an invoice. Attach one in Costs & invoice, or generate it there.",
    },
    chips: {
      fixed: 'Fixed',
      replaced: 'Replaced',
      inspected: 'Inspected',
      cleaned: 'Cleaned',
      needsFollowUp: 'Needs follow-up',
      unableToComplete: 'Unable to complete',
    },
    outcomes: {
      completed: 'Completed',
      partial: 'Partially completed',
      followUp: 'Needs follow-up',
      unable: 'Unable to complete',
    },
    step0: {
      title: "Postcheck",
      done: "Every check is complete. Continue to close out the job.",
      summary: "Answer every step of the postcheck. The job can’t be submitted until it’s finished.",
    },
  },
  // Role-based Job Detail experience — see
  // docs/current-state/role-based-job-experience.md
  /**
   * Job Detail used to have three peer modes (Do Work / Manage / Review) and a
   * segmented control to switch between them. They collapsed to ONE workspace
   * plus an execution focus over it; the read view is the completion report.
   * The `review.*` strings went with the mode — everything that card drew was a
   * worse re-render of the report, and its one unique verb (approve/reject) is
   * the dock's now, using the dock's own labels.
   */
  jobMode: {
    switcher: {
      /**
       * ONE toggle, not a picker — so each label names the surface it will
       * switch you TO, never the surface you are on.
       */
      enterFocus: 'Focus mode',
      exitFocus: 'Full job',
      a11yEnterFocus: 'Focus on doing the work — hide oversight detail',
      a11yExitFocus: 'Show the full job workspace',
    },
    report: {
      open: 'Open completion report',
      title: "Completion report",
      fallbackTitle: "Job",
      missingJob: "Job not found.",
      followOn: "Follow-up work",
      shareTitle: "Share completion report",
      shareAction: "Share",
      shareReviewLink: "Share review link",
      shareSheetTitle: "Share report",
      summaryEyebrow: "Close-out summary",
      notesEyebrow: "Completion notes",
      noNotes: "—",
      checksEyebrow: "Prechecks and postchecks",
      evidenceEyebrow: "Evidence and attachments",
      recentExports: "Recent exports",
      optionsTitle: "Report options",
      fields: {
        job: "Job",
        jobType: "Job type",
        parentJob: "Parent job",
        asset: "Asset",
        location: "Location",
        assignedTo: "Assigned to",
        status: "Status",
        submitted: "Submitted",
        verified: "Verified",
        completed: "Completed",
        actualCost: "Actual cost",
      },
    },
  },
  /** The asset's Maintenance area, the Record-reading sheet, and a plan's row in Work. */
  maintenance: {
    steps: {
      keep: "Keep steps",
      close: "Close",
      failed: "Couldn't save the steps",
      unlinked: "Steps unlinked. Each one is its own plan again.",
      unlinkConfirm: "Unlink",
      unlinkMessage: "Each step becomes its own plan again and keeps its schedule. Nothing is deleted.",
      unlinkTitle: "Unlink these steps?",
      unlink: "Unlink steps",
      saved: "Steps saved.",
      save: "Save steps",
      inOrderOff: "Each step comes round on its own schedule.",
      inOrderOn: "A step's work is raised only when the step before it has no open work.",
      inOrderTitle: "Do the steps in order",
      moveDownA11y: "Move {{name}} down",
      moveUpA11y: "Move {{name}} up",
      removeA11y: "Remove {{name}} from the steps",
      addA11y: "Add {{name}} as a step",
      add: "Add",
      inOtherSet: "Now a step of {{name}}",
      otherLabel: "Other plans on this asset",
      needTwo: "Add at least two plans from the list below.",
      stepsLabel: "Steps, in order",
      namePlaceholder: "For example: Generator services",
      nameLabel: "Name",
      intro: "Steps are plans that belong together, like a 500-hour, a 1,000-hour and a 2,000-hour service.",
      titleEdit: "Edit steps",
      titleNew: "Link plans as steps",
      addStep: "Add a step",
      editSteps: "Edit steps",
      linkSteps: "Link plans as steps",
    },
    list: {
      status: {
        cancelled: "Cancelled",
        ended: "Ended",
        paused: "Paused",
        active: "Active",
      },
      openJobA11y: "Open the current job for {{name}}",
      editA11y: "Edit {{name}}",
      untitled: "Untitled plan",
      groupA11y: "{{asset}}: {{n}} plans. Opens the asset.",
      emptyBody: "Add a plan for work that comes back — a monthly service, a yearly test. Each time it is due, the job appears in Work.",
      tryAgain: "Try again",
      loadFailed: "Could not load maintenance plans. Check your connection and try again.",
      activeMany: "{{live}} plans running",
      activeOne: "1 plan running",
      noneActive: "No plan is running. Resume one, or add a new one.",
      dueInDays: "Next due in {{days}} days",
      dueTomorrow: "Next due tomorrow",
      dueToday: "Next due today",
      overdueDays: "Overdue by {{days}} days",
      overdueOneDay: "Overdue by 1 day",
      doneCount: "{{done}} raised",
      doneOfMax: "{{done}} of {{max}} raised",
    },
    cadence: {
      fromLastDone: "{{cadence}}, counted from when the last one is done",
      oneOff: "One-off",
      either: "{{first}} or {{second}} — whichever comes first",
      everyAmount: "Every {{amount}}",
      everyNYears_one: "Every {{count}} year",
      everyNYears_other: "Every {{count}} years",
      everyYear: "Every year",
      everyNMonths_one: "Every {{count}} month",
      everyNMonths_other: "Every {{count}} months",
      everyMonth: "Every month",
      everyNWeeks_one: "Every {{count}} week",
      everyNWeeks_other: "Every {{count}} weeks",
      everyWeek: "Every week",
      everyNDays_one: "Every {{count}} day",
      everyNDays_other: "Every {{count}} days",
      everyDay: "Every day",
      everyNHours_one: "Every {{count}} hour",
      everyNHours_other: "Every {{count}} hours",
      everyHour: "Every hour",
    },
    title: 'Maintenance',
    recordReading: 'Record reading',
    addPlan: "Add plan",
    startNow: 'Start now',
    exceptions: {
      lead: 'Most skips are "{{reason}}" — {{share}}% of them.',
      counts: '{{skipped}} skipped · {{missed}} missed',
      emptyDimension: 'Nothing recorded against this yet — try another breakdown.',
      noSite: 'No site',
      unassigned: 'Unassigned',
      by: {
        reason: 'Reason',
        asset: 'Asset',
        site: 'Site',
        assignee: 'Assignee',
        programme: "Plan",
        team: 'Team',
      },
    },
    /** The asset tab's plan cards: title, description, how often, what's next. */
    card: {
      ok: "OK",
      actionFailedBody: "Nothing was changed. Check your connection and try again.",
      allPlans: "All plans",
      heading: "Maintenance plans",
      attention_one: '{{count}} plan needs attention',
      attention_other: '{{count}} plans need attention',
      allOnTrack: 'Every plan is on track',
      pausedCount_one: '{{count}} plan paused',
      pausedCount_other: '{{count}} plans paused',
      nextUp: 'Next: {{what}}',
      showAll: 'Show all {{count}} plans',
      showFewer: 'Show fewer',
      expandHint: 'Shows what is coming up and the plan actions',
      status: {
        job_overdue: 'Job open · overdue',
        overdue: 'Overdue',
        job_due: 'Job open · due now',
        due: 'Due now',
        no_reading: 'Needs a reading',
        due_soon: 'Due soon',
        due_soon_days_one: 'Due tomorrow',
        due_soon_days_other: 'Due in {{count}} days',
        due_soon_amount: 'Due in {{amount}}',
        job: 'Job open',
        waits: 'Waits for {{stage}}',
        waits_generic: 'Waits for the step before',
        afterStep: "After step {{n}}",
        skipped: 'Skipped this time',
        upcoming_date: 'Next {{when}}',
        upcoming_time: 'Next at {{time}}',
        upcoming_meter: 'Next at {{amount}}',
        upcoming_none: 'Runs when started',
        upcoming_condition: "Watching condition",
        paused: 'Paused',
      },
      cadence: {
        manual: 'When someone starts it',
        fixedDate: 'Once, on {{when}}',
        fixedDates_one: "On {{count}} set date",
        fixedDates_other: "On {{count}} set dates",
        condition: "When its condition drops to {{score}} or below",
        renewal: 'When its document is due for renewal',
        byMeter: {
          operating_hours: 'By machine hours',
          mileage: 'By mileage',
          cycles: 'By cycles',
          custom_numeric: 'By meter reading',
          electricity: "By electricity meter",
          gas: "By gas meter",
          water: "By water meter",
          heat: "By heat meter",
          oil: "By oil meter",
        },
      },
      comingUp: 'Coming up',
      lastDone: 'Last done',
      notDoneYet: 'Not done yet',
      perDay: '{{count}} a day after that',
      jobOpen: 'Job open',
      either: '{{amount}} or {{when}}, whichever first',
      nothingNext: 'Nothing is scheduled. It runs when someone starts it.',
      nothingNextCondition: "Work is raised when the condition score drops that far.",
      pausedNext: 'Nothing is raised while the plan is paused.',
      steps_sequential_one: '{{count}} step, done in order',
      steps_sequential_other: '{{count}} steps, done in order',
      steps_independent_one: "{{count}} plan in this group",
      steps_independent_other: "{{count}} plans in this group",
      step: 'Step {{n}}',
      openJob: 'Open the job',
      startNow: 'Start now',
      edit: 'Edit',
      bringForward: "Bring forward",
      endStep: "End step",
      pause: 'Pause',
      resume: 'Resume',
      end: 'End plan',
      endTitle: 'End this plan?',
      endMessage: 'No new work will be created. Work already raised stays in Work, and its history stays on the asset.',
      keep: 'Keep plan',
      actionFailed: 'Could not change the plan',
    },
    startFailedTitle: 'Could not start it',
    noReading: 'No meter reading yet.',
    noPlans: 'No maintenance plan yet.',
    /**
     * What this surface is, in one line, with the detail folded away.
     *
     * The one thing nobody guesses is that a plan can be due two ways at once,
     * so the summary says that and the rest stays shut until asked for.
     */
    plansHeading: "Plans · {{n}}",
    plansHeadingNone: "Plans",
    meters: {
      heading: "Meters · {{n}}",
      since: "{{amount}} since {{when}}",
      record: "Record a reading",
    },
    emptyReadingTitle: 'No reading recorded',
    emptyReadingBody: 'Plans that count by use need one to measure from.',
    emptyPlansTitle: "No plans yet",
    emptyPlansBody: "Tap Add plan for work that comes back — a service, a test or an inspection. Each one appears in Work when it is due.",
    kind: { mileage: 'Mileage', operating_hours: 'Hours', cycles: 'Cycles', electricity: "Electricity", gas: "Gas", water: "Water", heat: "Heat", oil: "Oil", custom_numeric: "Other" },
    unit: { mi: 'mi', km: 'km', h: 'h', cycles: 'cycles' },
    source: { manual: 'entered', local_ml: 'from photo', imported: 'imported', api: 'API', iot: 'sensor' },
    sync: {
      pending: 'Saved on this device — will sync when online.',
      failed: "Couldn't save — a higher reading was recorded first.",
    },
    rowTitle: {
      due_soon_meter: '{{title}} due in {{amount}}',
      due_meter: '{{title}} due now',
      overdue_meter: '{{title}} overdue by {{amount}}',
      due_soon_calendar: '{{title}} due soon',
      due_calendar: '{{title}} due today',
      overdue_calendar: '{{title}} overdue',
    },
    sheet: {
      typeReading: "Type the reading. A photo is kept as evidence; the phone app can read the number from it.",
      kind: "Meter",
      noMeterId: "No meter ID",
      newMeter: "New meter",
      meterId: "Meter ID (optional)",
      meterIdPlaceholder: "Serial or supply number",
      unit: "Unit",
      title: 'Record a meter reading',
      close: 'Close',
      value: 'Reading',
      when: 'Taken',
      whenInvalid: 'Pick a valid date and time.',
      currentIs: 'Current: {{amount}} · {{when}}',
      reading: 'Reading the photo on this device…',
      detected: 'Detected meter reading: {{amount}}',
      trust: {
        confirm: 'Confirm · read on this device, no AI credits used',
        check: 'Check this · read on this device, no AI credits used',
      },
      use: 'Use',
      ignore: 'Ignore',
      nothingDetected: 'No reading found in the photo — type it in. The photo is kept as evidence.',
      lower: 'This is lower than the current {{amount}}.',
      outOfOrder: 'A reading of {{amount}} is already recorded after this date. Check when this one was taken.',
      lowerAsk: 'Record it as a correction? The earlier reading stays in the history but stops counting.',
      lowerHint: 'Check the number, or ask a manager to record a correction.',
      correctionNote: 'Why is the earlier reading wrong?',
      correctionNoteRequired: 'Say why the earlier reading was wrong.',
      unitMismatch: 'This meter reads in {{unit}}.',
      save: 'Save reading',
      saveCorrection: 'Save as correction',
      saving: 'Saving…',
      failed: 'Could not save the reading.',
      savedOfflineTitle: 'Saved on this device',
      savedOffline: "It will sync when you're back online.",
      recent: 'Recent readings',
      correctionTag: 'correction',
    },
  },
  /**
   * The contractor's own work surface.
   *
   * Their vocabulary, not the workspace's: an external contractor should not
   * have to learn what "triage" or "verified" mean to answer "what do I do
   * next?". The five lenses are a pipeline (see `domain/work/contractorViews`),
   * so each one already states the status and the cards carry none.
   */
  contractor: {
    title: 'My work',
    view: {
      new: 'New',
      upcoming: 'To do',
      in_progress: 'In progress',
      awaiting_review: 'Waiting',
      complete: 'Done',
    },
    /** What an empty lens says. Their words, and never "nothing to triage". */
    empty: {
      new: 'No new requests right now',
      upcoming: 'Nothing booked in',
      in_progress: 'Nothing on the go',
      awaiting_review: 'Nothing with the client',
      complete: 'Nothing finished yet',
    },
    /** The line under an empty lens's title: what will land there. */
    emptyHint: {
      new: 'You will see invitations and quote requests here.',
      upcoming: 'Jobs you have accepted will appear here.',
      in_progress: 'Start a job from Upcoming when you get there.',
      awaiting_review: 'Work you submit waits here until they approve it.',
      complete: 'Work the client has approved will appear here.',
    },
    /**
     * The two moves the job lifecycle has no word for. The other four next-action
     * lines reuse `lifecycleGate.nextActorYou.*`, which already speaks in the
     * second person in every locale.
     */
    quoteFallbackTitle: 'Quote request',
    clientOther: 'Other',
    next: {
      quote: 'You send a price or decline',
      waitingClient: 'Waiting on the client',
    },
    notLinked: {
      title: 'Not set up as a contractor yet',
      body: 'Ask your client to add you by the email address you signed up with, and their work will appear here.',
      bodyWithEmail: 'Ask your client to add you as a contractor using {{email}}. Their jobs and quote requests will appear here.',
      checkAgain: 'Check again',
      checking: 'Checking…',
    },
    /** The work list did not load: never shown as "No new requests right now". */
    loadFailed: {
      title: "Your work didn't load",
      body: 'Check your connection, then tap Try again.',
      retry: 'Try again',
    },
  },
  planning: {
    lockedHint: "Put visits on people and times, see travel, and get a suggested plan for the day.",
    leave: {
      message_other: "You have {{count}} plan changes that are not saved yet. Discard them, or keep planning and press Apply.",
      message_one: "You have {{count}} plan change that is not saved yet. Discard it, or keep planning and press Apply.",
      discard: "Discard changes",
      keep: "Keep planning",
      title: "Leave without saving the plan?",
    },
    range: {
      day: 'Day',
      threeDay: "3 days",
      week: 'Week',
      month: 'Month',
      agenda: "2 weeks",
      a11y: 'Calendar range',
    },
    nav: {
      previousA11y: 'Earlier',
      nextA11y: 'Later',
      today: 'Today',
      todayA11y: 'Go to today',
    },
    lane: {
      unassigned: 'Unassigned',
      you: 'You',
      unnamed: 'Unnamed person',
      notWorking: 'Not working',
      timeOff: 'Time off',
      more: '+{{count}} more',
      moreA11y: 'Show {{count}} more at {{time}}',
      showLess: 'Show less',
      load: '{{percent}}% planned · {{work}}',
      capacityA11y: '{{name}}: {{percent}}% of the day is planned',
    },
    summary: {
      jobs_one: '{{count}} visit',
      jobs_other: '{{count}} visits',
      work: '{{duration}} work',
      travel: '{{duration}} travel',
      travelEstimated: 'about {{duration}} travel',
      travelUnknown_one: '{{count}} trip without a location',
      travelUnknown_other: '{{count}} trips without a location',
      free: '{{duration}} free',
      overtime: '{{duration}} overtime',
    },
    readiness: {
      ready: 'Ready {{count}}',
      parts: 'Need parts {{count}}',
      capacity: "Nobody free or skilled {{count}}",
      other: 'Waiting {{count}}',
      defer: 'Can wait {{count}}',
    },
    unplanned: {
      nobody: 'Has a time, nobody on it',
      title: 'Unplanned · {{count}}',
      empty: 'Everything in view is planned.',
      filterDue: 'Due',
      filterAll: 'All',
      filterA11y: 'Which unplanned work to show',
      due: 'Due {{date}}',
      overdue: 'Overdue',
    },
    reason: {
      parts_unavailable: 'Part unavailable',
      technician_unavailable: 'Nobody available',
      skill_missing: 'Skill unavailable',
      dependency: 'Waiting on other work',
      no_capacity: 'No capacity',
      location_missing: 'No location',
      appointment_conflict: 'Appointment cannot be met',
      outside_hours: 'Outside working hours',
      awaiting_approval: 'Awaiting approval',
      declined: 'Declined',
      crew_unavailable: 'Not enough people free at once',
    },
    signal: {
      overlap: 'Double-booked',
      insufficient_travel: 'Not enough travel time',
      skill_missing: 'Skill missing',
      parts_unavailable: 'Part unavailable',
      outside_appointment: 'Outside the appointment',
      outside_hours: 'Outside working hours',
      technician_unavailable: 'On time off',
      dependency_unmet: 'Waiting on other work',
      after_due: 'After the due date',
      urgent: 'High priority',
      parts_ready: 'Parts ready',
      parts_unknown: 'Parts not confirmed',
      appointment: 'Appointment window',
      awaiting_acceptance: 'Awaiting acceptance',
      confirmed: "Confirmed",
      locked: "Locked time",
      duration_default: 'Length is an estimate',
      duration_learned: 'Typical length',
      multi_day: 'Runs over several days',
      crew_visit: 'As one of the crew',
      via_pickup: 'Part pickup on the way',
    },
    state: {
      pending: 'Suggested',
      in_progress: 'In progress',
      done: 'Done',
    },
    travel: {
      block: '{{duration}} travel · {{distance}}',
      estimated: 'about {{duration}} travel · {{distance}}',
      a11y: 'Travel: {{duration}}',
      estimateNote: 'Travel is estimated from straight-line distance at city driving speed, with no traffic. Press a trip to check it in maps.',
      roadNote: 'Travel is typical road time, with no live traffic. Press a trip to check it in maps.',
      short: '{{duration}} travel',
      shortEstimated: 'about {{duration}} travel',
    },
    week: {
      legend: {
        work: 'Work',
        travel: 'Travel',
        free: 'Free',
        overtime: 'Beyond hours',
      },
      openDayA11y: 'Plan {{day}}',
      cellA11y: '{{name}}, {{day}}: {{percent}}% planned, {{work}} work, {{travel}} travel',
      cellOffA11y: '{{name}}, {{day}}: not working',
      percent: '{{percent}}%',
      free: 'Free',
      hoursUnknown: 'Hours not known',
      unplannedDue: 'Unplanned, due',
      forecast: 'With forecast work',
      forecastOver: 'Over by {{duration}}',
      forecastFits: 'Fits',
    },
    panel: {
      assignedTo: 'Assigned to',
      nobody: 'Nobody yet. Choose who does this.',
      partArrives: 'If the part arrives on',
      planFromThen: 'Plan from then',
      makeRoom: 'Make room',
      makeRoomHint: 'This lands on another visit. Offer that visit a new place.',
      day: 'Day',
      start: 'Start',
      length: 'Length',
      ends: 'Ends {{time}}',
      notPlanned: 'Not planned',
      moreNotes_one: '{{count}} more note',
      moreNotes_other: '{{count}} more notes',
      fewerNotes: 'Fewer notes',
      staged: 'Not saved yet',
      fixed: 'This work has started or is confirmed, so it stays where it is.',
      awaitingApproval: 'Awaiting approval. It cannot be planned yet.',
      previousDayA11y: 'The day before',
      nextDayA11y: 'The day after',
      earlierA11y: 'Start 15 minutes earlier',
      laterA11y: 'Start 15 minutes later',
      shorterA11y: '15 minutes shorter',
      longerA11y: '15 minutes longer',
      remove: 'Remove from plan',
      pin: "Lock this time",
      unpin: "Unlock time",
      undo: 'Undo change',
      open: 'Open',
      closeA11y: 'Close the plan panel',
    },
    apply: {
      changes_one: '{{count}} change not saved',
      changes_other: '{{count}} changes not saved',
      notifies: 'Applying tells {{names}}',
      discard: 'Discard',
      apply: 'Apply',
      breaks: 'Breaks a rule: {{rules}}',
      reasonLabel: 'Reason for going ahead',
      reload: 'Reload',
      done: 'Plan saved',
      conflict: 'Someone else changed {{days}} while you were planning. Reload to see it. Your changes are kept.',
      failed_one: '{{count}} change could not be saved: {{message}}',
      failed_other: '{{count}} changes could not be saved. The first: {{message}}',
      error: 'The plan could not be saved. {{message}}',
      /** The server read the rows back: only some of what was asked for is there. Never "saved". */
      partial: '{{applied}} of {{requested}} changes applied',
      none_one: 'The change could not be applied',
      none_other: 'None of the {{count}} changes could be applied',
      unsettled: 'The changes were written but the result could not be confirmed. Reload to check the plan.',
      reviewIssues_one: 'Review {{count}} issue',
      reviewIssues_other: 'Review {{count}} issues',
      issues: {
        title: 'Changes that did not apply',
        appliedLine_one: '{{count}} change applied',
        appliedLine_other: '{{count}} changes applied',
        retry: 'Retry failed',
        keep: 'Keep applied changes',
        discardRest: 'Discard remaining',
        open: 'Open',
        closeA11y: 'Close the review of changes that did not apply',
        code: {
          refused: "Not allowed",
          not_persisted: "Did not save",
          unverified: "Could not check it saved",
          missing: 'No longer exists',
        },
      },
      /** An Apply the app did not finish, found on the next visit. */
      recover: 'An earlier plan update did not finish.',
      recoverAction: 'Review',
      recovered: {
        applied: 'The earlier update had applied in full.',
        failed: 'None of the earlier update had applied.',
      },
      /** The staged proposal was prepared on days that have changed since. */
      stale: "Someone changed {{days}} after you made these changes. Check the board, then keep or discard your changes.",
      staleAction: "Keep my changes",
      overrideNote: '{{rule}}, overridden: {{reason}}',
    },
    optimise: {
      action: "Suggest a plan",
      title: 'Suggested plan',
      whatIfA11y: 'What if {{name}} is off?',
      whyShow: "Why this plan",
      whyHide: "Hide the reasons",
      scenarioOff: "What if {{name}} is off",
      scenarioArrival: "What if the part arrives on {{date}}",
      scenarioNote: "Only a preview. Nothing changes until you press Use this plan.",
      working: "Working out the plan… {{seconds}} s",
      stop: "Stop",
      useScenario: 'Use this plan',
      closeA11y: 'Close the suggested plan',
      removeA11y: 'Leave {{title}} as it is',
      why: {
        placed: 'Placed because: {{list}}.',
        held: 'Still unplanned: {{list}}.',
        reason: '{{count}} × {{reason}}',
        blocker: '{{count}} × {{reason}}',
        join: ' · ',
      },
      whyReason: {
        required_skill: 'the required skill',
        parts_ready: 'parts ready',
        due_today: 'due that day',
        urgent: 'high priority',
        near_previous: 'near the stop before',
        same_site: 'already at the site',
        keeps_assignee: 'stays with the same person',
        saves_travel: 'saves travel',
        after_overrun: 'after the overrun',
        from_position: 'from where they are',
        overtime: 'past the day',
        with_crew: 'with the crew',
        via_pickup: 'via a pickup',
        swapped: 'swapped between people',
        fits_capacity: 'fits the day',
      },
      extraPerson: 'What if one more person?',
      extraPersonTakes_one: 'One more person would take {{count}} more visit.',
      extraPersonTakes_other: 'One more person would take {{count}} more visits.',
      extraPersonNone: 'One more person would not change the plan.',
      none: 'Nothing to improve: the plan already fits.',
      planned: 'Planned {{before}} → {{after}}',
      travel: 'Travel {{before}} → {{after}}',
      stillUnplanned_one: '{{count}} still unplanned',
      stillUnplanned_other: '{{count}} still unplanned',
      confidence: {
        high: 'High confidence',
        medium: 'Medium confidence',
        low: 'Low confidence',
      },
      note: {
        parts_unknown_one: 'Parts not confirmed for {{count}} visit',
        parts_unknown_other: 'Parts not confirmed for {{count}} visits',
        location_missing_one: '{{count}} visit has no location',
        location_missing_other: '{{count}} visits have no location',
        duration_default_one: '{{count}} visit uses an estimated length',
        duration_default_other: '{{count}} visits use an estimated length',
        travel_estimated_one: 'Travel is estimated for {{count}} trip',
        travel_estimated_other: 'Travel is estimated for {{count}} trips',
      },
      reason: {
        required_skill: 'Has the required skill',
        parts_ready: 'Parts ready',
        due_today: 'Due that day',
        urgent: 'High priority',
        near_previous: '{{duration}} from the stop before',
        same_site: 'Already at this site',
        fits_capacity: 'Fits the day',
        keeps_assignee: 'Stays with the same person',
        saves_travel: 'Saves {{duration}} of travel',
        after_overrun: 'Starts when the job before can finish',
        from_position: 'Starts when they can get there from where they are',
        overtime: '{{duration}} past the working day',
        with_crew: 'Placed with the crew at the same time',
        via_pickup: 'Goes via the part pickup',
        swapped: 'Swapped with another person, saving {{duration}}',
      },
    },
    schedulePlan: {
      save: 'Save plan',
      failed: 'Could not save. {{message}}',
    },
    myDay: {
      boardHint: "You can see the whole team's day here. To move work between people on the day board, open Work on a tablet or computer.",
      title: "My Day",
      saved: 'Saved {{time}} · no connection, showing the last copy',
      savedWide: 'Saved {{time}} · the server could not be reached, showing the last copy. Nothing can be applied until it can.',
      changed: 'Your plan changed while you were away: {{moved}} moved, {{added}} new, {{removed}} gone.',
      changedDismissA11y: 'Dismiss the note about what changed',
      mine: "My Day",
      everyone: 'Everyone',
      switchA11y: 'Whose day to show',
      accept: 'Accept',
      decline: 'Decline',
      accepted: 'Accepted',
      acceptFailed: 'Could not accept. {{message}}',
      startNext: 'Start next: {{title}}',
    },
    insights: {
      eyebrow: 'Work Planning',
      title: 'How the plan is going',
      subtitle: 'The last {{days}} days',
      none: '—',
      utilisation: 'Working, not driving',
      utilisationCaption: 'of planned time',
      utilisationInfo: 'Work as a share of work plus travel over every planned day in the period. Travel is never productive time.',
      adherence: 'Started as planned',
      adherenceCaption_one: 'of {{count}} planned visit',
      adherenceCaption_other: 'of {{count}} planned visits',
      adherenceInfo: 'Planned visits that started within {{minutes}} minutes of their planned start.',
      actual: 'Actual vs planned length',
      actualCaption_one: 'median of {{count}} visit',
      actualCaption_other: 'median of {{count}} visits',
      actualInfo: 'How long finished visits really took as a share of the length they were planned at. 100% is as planned; more means plans run short.',
    },
    directions: {
      action: 'Directions',
      route: 'Route',
      routeA11y: 'Open the route for {{name}} in maps',
      legA11y: 'Opens this trip in maps',
      partsTitle: 'This route opens in parts',
      partsBody: 'A maps link holds a limited number of stops. Choose the part to open.',
      part: 'Stops {{from}}–{{to}}',
      cancel: 'Cancel',
    },
    map: {
      show: 'Map',
      hide: 'Board',
      hideNarrow: 'List',
      empty: 'Nothing planned on this day has a location.',
      unplannedNearby_one: '{{count}} unplanned, with a location',
      unplannedNearby_other: '{{count}} unplanned, with a location',
      stopA11y: 'Stop {{number}}: {{title}}',
      noLocation_one: '{{count}} with no location',
      noLocation_other: '{{count}} with no location',
      legend: 'Dashed lines join the stops in order. They are not the road.',
    },
    home: {
      title: 'Today’s plan',
      count_one: '{{count}} visit',
      count_other: '{{count}} visits',
      withNext_one: '{{count}} visit · next {{time}} {{title}}',
      withNext_other: '{{count}} visits · next {{time}} {{title}}',
    },
    settings: {
      title: 'Planning settings',
      closeA11y: 'Close planning settings',
      week: 'Working week',
      starts: 'Day starts',
      ends: 'Day ends',
      break: 'Break',
      noBreak: 'None',
      defaults: 'When nobody said',
      jobLength: 'A job takes',
      inspectionLength: 'An inspection takes',
      citySpeed: 'City driving',
      citySpeedHint: 'Used to estimate travel when no road time is known. Longer trips are assumed faster.',
      save: 'Save',
      failed: 'Could not save. {{message}}',
      person: 'For {{name}}',
      personHours: 'Their hours',
      ownHours: 'Their own',
      sameHours: 'Same as the workspace',
      personDepot: "Where their day starts and ends",
      savePerson: 'Save for this person',
      overtime: "Suggested plans may run past the day by",
      noOvertime: 'Never',
      overtimeHint: "Suggested plans avoid this when they can. A change past the day is marked and needs a reason before you apply it.",
      depot: "Where the day starts and ends",
      noDepot: 'Not set',
      depotHint: 'A site. The first and last trips of the day count from here.',
      usePositions: 'Use where people are',
      usePositionsHint: 'Each person chooses whether to share, and only while the app is open. Only owners and admins see it, to spot someone running late. Only the latest position is kept, for two hours.',
      clearDepot: 'Clear',
      lessA11y: 'Less: {{label}}',
      moreA11y: 'More: {{label}}',
      timeOff: 'Time off on {{day}}',
      removeOffA11y: 'Remove time off for {{name}}',
      off: {
        allDay: 'All day',
        morning: 'Morning',
        afternoon: 'Afternoon',
      },
    },
    parts: {
      title: 'Parts needed',
      empty: 'No parts listed. Add what someone must bring.',
      addLabel: 'Part or material',
      add: 'Add part',
      removeA11y: 'Remove {{part}}',
      failed: 'Could not save the part. {{message}}',
      availability: {
        unknown: 'Not confirmed',
        available: 'Available',
        reserved: 'Reserved',
        unavailable: 'Unavailable',
      },
      heldAt: 'Held at {{site}}',
      notHeld: 'Where is it held?',
      clearHeld: 'Clear',
    },
    jobPlanning: {
      title: 'Planning',
      people: 'People needed at once',
      fewerA11y: 'Fewer people',
      moreA11y: 'More people',
      appointment: 'Must happen between',
      on: 'On',
      from: 'From',
      to: 'To',
      saveAppointment: 'Save window',
      clearAppointment: 'Clear window',
      window: '{{on}}, {{from}}–{{to}}',
      noWindow: 'Any time',
      waitsOn: 'Waits on',
      waitsOnNone: 'Nothing',
      removeDepA11y: 'No longer waits on {{title}}',
      addDepA11y: 'Wait on {{title}}',
      failed: 'Could not save. {{message}}',
    },
    refine: {
      planningTitle: 'Planning',
      partsTitle: 'Parts',
      planning: {
        planned: 'Planned',
        unplanned: 'Unplanned',
        blocked: 'Blocked',
      },
      parts: {
        ready: 'Ready',
        unavailable: 'Unavailable',
        unknown: 'Not confirmed',
      },
    },
    lanes: {
      a11y: 'Columns show',
      people: 'By person',
      site: 'By site',
      asset: 'By asset',
      noSite: 'No site',
      noAsset: 'No asset',
    },
    review: {
      overrun: '{{title}} is running {{duration}} over. The rest of the day for {{name}} no longer fits.',
      absence_one: '{{name}} is off over {{count}} planned visit.',
      absence_other: '{{name}} is off over {{count}} planned visits.',
      urgent_one: '{{count}} urgent job is not planned.',
      urgent_other: '{{count}} urgent jobs are not planned.',
      part_unavailable: 'A part for {{title}} is no longer available.',
      cancelled: '{{title}} was cancelled: its time is free.',
      early_finish: '{{name}} finished {{title}} early: there is time for more.',
      running_late: '{{name}} will be about {{duration}} late for {{title}}, from where they are.',
      more: '+{{count}} more',
      action: 'Review plan',
      dismissA11y: 'Set this aside',
      nothing: 'Nothing can be moved to fix this. It needs a decision by hand.',
    },
    position: {
      stop: "Stop sharing",
      share: "Share location",
      sharingShort: 'Sharing your location',
      stopA11y: 'Stop sharing your location',
      offer: 'Share where you are while you work, so your day can be adjusted if you are running late.',
      explainTitle: 'Share where you are?',
      explain: 'While this app is open and you have work today, your location is sent every few minutes. Only your workspace owners and admins see it, to spot when you will be late for your next visit. Only the latest location is kept, it is erased after two hours, and you can stop at any time.',
      explainConfirm: 'Share while the app is open',
      explainCancel: 'Not now',
      denied: 'Location is off for this app. Allow it in your device settings to share where you are.',
      failed: 'Could not change sharing. {{message}}',
      fromYou: 'About {{duration}} from you to {{title}}',
      seen: 'Seen {{duration}} ago',
      seenNow: 'Seen just now',
    },
    slot: {
      createA11y: 'Create work at {{time}}',
      clear: 'No clash with their day',
    },
    rail: {
      allDay: 'All day',
      forecast: 'Forecast',
      forecastA11y: 'Forecast, not booked yet: {{title}}',
    },
    agenda: {
      empty: 'Nothing planned for this day. The arrows move to other days.',
    },
    error: {
      tryAgain: "Try again in a moment.",
      offline: "Check your connection and try again.",
      notAllowed: "You don’t have permission to change this.",
      load: 'The plan could not be loaded.',
      retry: 'Retry',
    },
    loadingA11y: 'Loading the plan',
    truncated: 'There is more work on these days than the calendar loads. Narrow it with Refine.',
  },
  /** The planning sheet (components/work/WorkScheduler.tsx). */
  scheduler: {
    contractor: {
      giveNow: "Give to contractor",
      pick: "Pick a contractor",
      change: "{{name}} · Change",
      hint: "They get it in their My work and need an AssetWRX account. You still sign it off.",
      missing: "Pick the contractor this inspection goes to.",
    },
    fitLine: {
      bestAvailable: "Best available match",
      goodFit: "Good fit",
      line: "Best match: {{name}} · {{score}}% fit · {{why}}",
    },
    fitReason: {
      certification: "Has a required certification",
      withinRange: "Within travelling distance",
      closeToSite: "Close to site",
      available: "Marked available",
      team: "On the team responsible for this work",
      capacity: "Has time free",
      lightlyBooked: "Only {{n}}h booked",
      nothingBooked: "Nothing booked",
      onTime: "Usually completes on time",
      verifiedHistory: "Finished work is usually verified",
      assetTypeExperience: "Experience with this asset type",
      priorWorkOnAsset: "Prior work on this asset",
      skillsMatch: "Has the skills this job needs",
      categoryMany: "{{n}} completed jobs in this category",
      categoryOne: "1 completed job in this category",
    },
    blocker: {
      not_assignable: "Cannot be assigned work",
      inactive_contractor: "Contractor is not active",
      no_site_access: "No access to this site",
      unavailable: "Unavailable at that time",
      missing_certification: "Missing a required certification",
      missing_mandatory_skill: "Missing a required skill",
    },
    slotReason: {
      afterDue: "After the due date",
      sameSite: "Same site as the job before it",
      beforeDue: "Before the due date",
      minutes: "{{n}}-minute",
      hours: "{{n}}-hour",
      gapFree: "A free {{duration}} slot",
      firstGap: "The first free {{duration}} slot",
    },
    notes: {
      revert: "Revert",
      discard: "Discard",
      accept: "Accept",
      tidyFailedTitle: "Could not tidy up the note",
      tidiedReview: "Check it, then tap Accept.",
      tidiedTitle: "Tidied-up note",
      tidyUp: "Tidy up with AI",
      stop: "Stop",
      dictate: "Dictate",
    },
    assignee: {
      noMatch: "No one on your team matches.",
      search: "Search your team",
      blocked: "not available",
      contractor: "Contractor",
      technician: "Technician",
      notEligibleItem: "{{name}} ({{reason}})",
      notEligible: "Cannot take this: {{list}}",
      bestMatch: "Best match: {{name}}",
      assignA11y: "Assign {{name}}",
      recommended: "Recommended: {{name}}",
      youSuffix: "{{name}} (you)",
      you: "You",
    },
    invite: {
      description: "Checklist: {{checklist}}",
      title: "Inspection: {{asset}}",
    },
    errors: {
      photosOtherWorkspace: "Your photos are kept for the workspace you made this job in. Switch back to it, then tap Finish saving job.",
      otherWorkspace: "Switch back to the workspace you made this job in, then tap Finish saving job.",
      pickTime: "Pick a date and a time, then try again.",
    },
    errorTitles: {
      reschedule: "New time not saved",
      jobLater: "Job not scheduled",
      jobNow: "Job not created",
      inspectionLater: "Inspection not scheduled",
      inspectionNow: "Inspection not started",
    },
    checklist: {
      noMatch: "No checklist matches. Try another word, or tap New checklist.",
      search: "Search checklists",
      add: "Add",
      done: "Done",
      change: "Change",
      picked: "You picked this one.",
      loadFailed: "Your checklists did not load.",
      loading: "Loading checklists…",
    },
    slotAfterDue: "after the due date",
    moreSummaryShort: "How to run it and reminders",
    renewing: "Renewing {{title}}. This inspection records the evidence for the renewal.",
    renewingJob: "Renewing {{title}}. When this job is done, create the replacement certificate from it.",
    renewJobTitle: "Renew {{title}}",
    fromRequest: "From request: {{title}}",
    runMode: {
      list: "List",
      guided_group: "By section",
      guided_item: "One step at a time",
    },
    reminder: {
      "2day": "2 days before",
      "1day": "1 day before",
      "2hr": "2 hours before",
      "1hr": "1 hour before",
      "30min": "30 minutes before",
      "15min": "15 minutes before",
      at_time: "At scheduled time",
    },
    cta: {
      scheduleJob: "Schedule job",
      scheduleInspection: "Schedule inspection",
      createJob: "Create job",
      startInspection: "Start inspection",
      saveTime: "Save new time",
      finishJob: "Finish saving job",
    },
    heading: {
      job: "Job · {{asset}}",
      inspection: "Inspection · {{asset}}",
      rescheduleJob: "Reschedule job",
      rescheduleInspection: "Reschedule inspection",
    },
    done: {
      jobCreated: "Job created for {{asset}}.",
      jobScheduled: "Job for {{asset}} booked for {{when}}.",
      jobCreatedPhotos: "Job created. Photos saved on this device for upload.",
      jobRescheduled: "Job for {{asset}} moved to {{when}}.",
      open: "Open",
      inspectionRepeats: "Inspection for {{asset}} booked for {{when}}. Repeats: {{how}}.",
      inspectionScheduled: "Inspection for {{asset}} booked for {{when}}. It shows in Work on that day.",
      inspectionRescheduled: "Inspection for {{asset}} moved to {{when}}.",
    },
    hide: "Hide",
    moreSummary: "How to run it, reminders, notes and who does it",
    errorBody: "Nothing was saved. Check your connection and try again.",
    errorPartialTitle: "Job created; saving is incomplete",
    repeat: {
      advance: {
        on_calendar: "On the planned dates",
        on_completion: "After this one is done",
      },
      nextFrom: "When is the next one due?",
      noLimit: "No limit",
      maxTimes: "Stop after (times)",
      use: "Use {{how}}",
      suggested: "Suggested: {{how}}. {{why}}",
      planHint: "To repeat by meter reading or on set dates, add a maintenance plan on the asset instead.",
      errorMax: "Enter how many times, or leave it blank.",
      errorEndBeforeStart: "The repeat cannot end before the first visit.",
      errorEnd: "Pick the day the repeat ends.",
    },
    show: "Show",
  },
  workItemCopy: {
    issues_one: "{{count}} issue",
    issues_other: "{{count}} issues",
    inspectionFallback: "Inspection",
  },
  assistantCards: {
    draftFindingPhoto: "Draft a finding from the photo",
    suggestTriagePriority: "Suggest a triage priority",
    recurringRaisesPriority: "A recurring issue would raise the priority.",
    suggestRepair_one: "Suggest a repair for {{count}} failed item",
    suggestRepair_other: "Suggest a repair for {{count}} failed items",
    checkExistingRepair: "Check the existing repair first — it may be a duplicate.",
    draftFindingEvidence: "Draft a finding from the evidence",
    suggestPriority: "Suggest a priority",
    suggestContractor: "Suggest the best-matched contractor",
    contractorRecurring: "One contractor keeps resolving work on this asset.",
    verifyEvidence: "Verify the completion evidence",
    suggestFollowUp: "Suggest a follow-up action",
    verifyBeforeSignOff: "Verify the completion evidence before sign-off",
    summariseJourney: "Summarise the journey for the report",
    summarisePortfolio: "Summarise the portfolio journey",
    portfolioRisk: "Portfolio risk {{band}} ({{score}}/100)",
    riskBandLow: "low",
    riskBandMedium: "medium",
    riskBandHigh: "high",
  },
  evidenceLocal: {
    identifierVin: "VIN {{value}}",
    identifierSerial: "Serial {{value}}",
    identifierOther: "Identifier {{value}}",
    codes_one: "{{count}} code",
    codes_other: "{{count}} codes",
    readings_one: "{{count}} reading",
    readings_other: "{{count}} readings",
    complianceLabel: "{{scheme}} label",
    dates_one: "{{count}} date",
    dates_other: "{{count}} dates",
    recognised: "Recognised {{label}}. No structured data found on device.",
    nothingFound: "No information extracted on device.",
    found: "Found {{parts}} on device.",
    separator: ", ",
    mobileOnly: "On-device analysis runs on the mobile app.",
    failed: "Could not analyse evidence on device.",
  },
  fitRisks: {
    missesDueDates: "Often misses due dates",
    repeatReopens: "Repeat reopens on recent work",
    someRework: "Some rework on past jobs",
    hoursCommitted_one: "{{count}}h already committed",
    hoursCommitted_other: "{{count}}h already committed",
    heavyWorkload: "Heavy current workload",
    elevatedWorkload: "Elevated active workload",
    overdueAssignments_one: "{{count}} overdue assignment",
    overdueAssignments_other: "{{count}} overdue assignments",
  },
  journeySignals: {
    whereAsset: "on this asset",
    wherePortfolio: "across the portfolio",
    alreadyRepairedLabel: "A repair {{where}} has already been completed.",
    alreadyRepairedDetailAgo_one: "The most recent completed job {{where}} closed {{count}}d ago — check it is not a duplicate before raising new work.",
    alreadyRepairedDetailAgo_other: "The most recent completed job {{where}} closed {{count}}d ago — check it is not a duplicate before raising new work.",
    alreadyRepairedDetail: "A completed job already exists {{where}} — check it is not a duplicate before raising new work.",
    recurringLabel_one: "{{count}} separate job or finding recorded {{where}}.",
    recurringLabel_other: "{{count}} separate jobs or findings recorded {{where}}.",
    recurringDetail_one: "{{count}} work item {{where}} reached a job or finding in the last {{days}} days — a repeat-maintenance asset may warrant a root-cause review rather than another one-off fix.",
    recurringDetail_other: "{{count}} work items {{where}} reached a job or finding in the last {{days}} days — a repeat-maintenance asset may warrant a root-cause review rather than another one-off fix.",
    contractorLabel_one: "{{name}} has resolved {{count}} job {{where}}.",
    contractorLabel_other: "{{name}} has resolved {{count}} jobs {{where}}.",
    contractorDetail: "Repeated callouts to the same contractor can signal an unresolved root cause.",
    healthLabel_one: "Asset health is deteriorating (down {{count}} point).",
    healthLabel_other: "Asset health is deteriorating (down {{count}} points).",
    healthDetail: "Operational health fell from {{then}} to {{now}} — prioritise this asset before it reaches an Action posture.",
    overdueLabel_one: "{{count}} open follow-up {{where}} is overdue.",
    overdueLabel_other: "{{count}} open follow-ups {{where}} are overdue.",
    overdueDetail: "Open journeys {{where}} have breached at least one stage SLA — they are at risk of slipping further.",
  },
  suggestJobCopy: {
    noInspectionData: "No inspection data supplied to suggest jobs.",
    noSuggestions: "No job suggestions for this inspection.",
    suggestionsReady_one: "{{count}} job suggestion ready.",
    suggestionsReady_other: "{{count}} job suggestions ready.",
    addRequestSummary: "Add a request summary to suggest a job.",
    derivedFromRequest: "Job suggestion derived from request.",
    derivedWarning: "Derived from the request (no AI). Confirm before creating the job.",
    enableAi: "Enable AI to suggest a follow-up job.",
    enableAiHint: "Use AI to generate a follow-up job suggestion.",
    offline: "AI suggestion unavailable offline.",
    offlineWarning: "AI suggestion unavailable offline",
    followUpFailed: "Could not suggest a follow-up job.",
    notAvailable: "Job suggestions are not available for this context.",
    suggestionFailed: "Suggestion failed",
    jobFailed: "Could not suggest a job.",
  },
  rules: {
    title: 'Group rules',
    subtitle: 'Repeat one checklist on many assets',
    intro: 'A group rule puts the same checklist on every matching asset, such as every fire door in Building A each month. New assets that match join it by themselves.',
    howTitle: 'How group rules work',
    howStep1: 'Pick the group: a kind of asset, one of your asset types, a kind of place or a zone. You can keep it to what is inside one place.',
    howStep2: 'Pick the checklist, how often, and the date of the first inspection.',
    howStep3: 'Saving makes one inspection for every asset in the group. An asset that joins later gets its own; one that leaves has its not-started inspection cancelled.',
    howExample: 'Example: "Monthly fire door check" takes every Fire door inside Building A. Saving makes 24 inspections, due on the first date. On the Work page they show as one row.',
    howEdits: 'Editing a rule updates its inspections that have not started. Started and finished inspections never change. Stopping a rule cancels the ones not started.',
    howTour: 'Show me',
    howGuide: 'Read the guide',
    add: 'Add group rule',
    newTitle: 'New group rule',
    editTitle: 'Edit group rule',
    nameLabel: 'Name',
    namePlaceholder: 'Monthly fire door check',
    nameRequired: 'Give the rule a name',
    whatLabel: 'Which assets',
    whatHint: 'Pick a kind of asset first.',
    anyPlace: 'Any place',
    whereLabel: 'Where',
    whereHint: 'Inside a place takes everything in it at every depth: a building\'s floors, their rooms, and what is fitted in them.',
    anywhere: 'Anywhere',
    aPlace: 'a place',
    someType: 'An asset type',
    anyAsset: 'Any asset',
    inZone: 'in zone {{zone}}',
    insidePlace: 'inside {{place}}',
    assignedTo: 'to {{name}}',
    zoneLabel: 'Zone (optional)',
    zoneHint: 'Only assets tagged with this zone.',
    checklistLabel: 'Checklist',
    pickChecklist: 'Pick a checklist',
    checklistRequired: 'Pick a checklist',
    searchChecklists: 'Search checklists',
    howOftenLabel: 'How often',
    startsOnLabel: 'First inspection on',
    startsOnInvalid: 'Enter a date as YYYY-MM-DD',
    assigneeLabel: 'Assign to (optional)',
    assigneeHint: 'They are told once, however many inspections the rule makes.',
    nobody: 'Nobody yet',
    someone: 'Someone',
    you: '{{name}} (you)',
    searchPeople: 'Search people',
    needsGroup: 'Pick a kind of asset, an asset type, a kind of place or a zone.',
    sentence: '{{recurrence}} from {{date}}: each {{what}}{{where}} gets the checklist "{{checklist}}".',
    sentenceNoChecklist: '{{recurrence}} from {{date}}: each {{what}}{{where}}. Pick a checklist to finish.',
    sentenceNeedsGroup: 'Say which assets to see the rule here.',
    sentenceInside: ' inside {{place}}',
    sentenceInZone: ' in zone {{zone}}',
    sentenceAssigned: 'Assigned to {{name}}.',
    counting: 'Counting…',
    estimate_one: 'It takes {{count}} asset right now.',
    estimate_other: 'It takes {{count}} assets right now.',
    estimateHint: 'An asset that joins the group later gets its own inspection, due the day it joins.',
    estimateOffline: 'Counted from the assets on this device, because the server could not be reached. The rule still covers every match.',
    editUpdates_one: 'Saving updates {{count}} inspection that has not started.',
    editUpdates_other: 'Saving updates {{count}} inspections that have not started.',
    editLeaving_one: '{{count}} inspection will be cancelled: its asset is no longer in the group.',
    editLeaving_other: '{{count}} inspections will be cancelled: their assets are no longer in the group.',
    editJoining_one: '{{count}} new inspection will be made.',
    editJoining_other: '{{count}} new inspections will be made.',
    editKept: 'Started and finished inspections are not changed.',
    cancel: 'Cancel',
    save: 'Save rule',
    saved_one: 'Saved. {{count}} inspection is planned.',
    saved_other: 'Saved. {{count}} inspections are planned.',
    failedTitle: 'Couldn\'t save the rule',
    failedBody: 'Check your connection and try again.',
    active: 'Active',
    stopped: 'Stopped',
    savedMoreComing_one: 'Saved. {{count}} inspection is planned so far; the rest are added or updated in the next few minutes.',
    savedMoreComing_other: 'Saved. {{count}} inspections are planned so far; the rest are added or updated in the next few minutes.',
    moreComing: 'Still adding or updating inspections. Check back in a few minutes.',
    openVisits_one: '{{count}} open inspection',
    openVisits_other: '{{count}} open inspections',
    fromRuleLabel: 'From group rule',
    edit: 'Edit',
    stop: 'Stop',
    restart: 'Restart',
    stopConfirmTitle: 'Stop this rule?',
    stopConfirmBody_one: 'Its {{count}} inspection that has not started will be canceled. Finished inspections stay.',
    stopConfirmBody_other: 'Its {{count}} inspections that have not started will be canceled. Finished inspections stay.',
    emptyTitle: 'No group rules yet',
    emptyBody: 'An owner or admin sets these up. Their inspections then appear on the Work page like any other.',
    emptyBodyAdmin: 'Add your first rule to check a whole group of assets on one schedule.',
    foldedRow_one: '{{name}} · {{count}} asset',
    foldedRow_other: '{{name}} · {{count}} assets',
  },
  rounds: {
    title: 'Start a round',
    intro: 'One job that walks through the places and items inside {{place}}. Each stop gets its own checklist.',
    stopsLabel: 'Stops',
    stopCount_one: '{{count}} stop',
    stopCount_other: '{{count}} stops',
    all: 'All',
    none: 'None',
    loading: 'Reading what is inside…',
    tooMany: 'A round can have at most {{max}} stops. Untick some.',
    titleLabel: 'Round name',
    defaultTitle: '{{checklist}} round: {{place}}',
    dateLabel: 'Date (optional)',
    dateHint: 'YYYY-MM-DD. Leave it empty to plan it later.',
    description_one: 'Round of {{count}} stop in {{place}}.',
    description_other: 'Round of {{count}} stops in {{place}}.',
    create_one: 'Start round with {{count}} stop',
    create_other: 'Start round with {{count}} stops',
    created_one: 'Round started with {{count}} stop',
    created_other: 'Round started with {{count}} stops',
    failedTitle: 'Couldn\'t start the round',
    failedBody: 'Nothing was made. Check your connection and try again.',
    emptyTitle: 'Nothing to visit here',
    emptyBody: 'Add places or equipment inside this place first, or change the filters.',
  },
} as const;
